import type {
  EntitySaveOptions,
  InvoiceMailLogEntry,
  InvoiceMailPreview,
  InvoiceMailPreviewInput,
  InvoiceMailSendInput,
  InvoiceMailSendResult,
  InvoiceMailState,
  MailAccountInput,
  MailAddressSuggestion,
  MailAttachmentAction,
  MailAttachmentOptions,
  MailAttachmentSource,
  MailCompanyInput,
  MailCompanyPatch,
  MailConnectionVerification,
  MailIdentityCreateInput,
  MailIdentityDnsResult,
  MailIdentityPatchInput,
  MailIdentityTestInput,
  MailIdentityTestResult,
  MailListFilter,
  MailPushContent,
  MailSnoozeGroup,
  MailTemplateInput,
  MailTemplatePatch,
  PublicMailCompany,
  PublicMailIdentity,
  PublicMailTemplate,
} from '@embertime/shared'
import { type MailAssistResult, PRIMARY_INVOICE_PDF_FORMAT } from '@embertime/shared'
import { oauthLink } from './oauth-link'

// All callers already include /api, including uploads and download URLs.
const BASE_URL =
  (import.meta.env.VITE_API_URL as string | undefined) ||
  (import.meta.env.BASE_URL === '/'
    ? 'http://localhost:4000'
    : new URL('.', document.baseURI).href.replace(/\/$/, ''))

export const API_BASE_URL = BASE_URL

type UnauthorizedListener = (status: number, path: string) => void
let unauthorizedListener: UnauthorizedListener | null = null

/** Session end (#834): every 401 answer is reported with its API path; the
 * listener (lib/session-end.ts) decides whether the session is over. */
export function onUnauthorized(listener: UnauthorizedListener): () => void {
  unauthorizedListener = listener
  return () => {
    if (unauthorizedListener === listener) unauthorizedListener = null
  }
}

/** API path of a request URL (#835): strips the API base (host plus base path
 * such as /zeit) so the listener sees `/api/…` and can recognise auth endpoints. */
export function apiPathOf(url: string, base: string = BASE_URL): string {
  if (url.startsWith(`${base}/`)) return url.slice(base.length)
  if (url.startsWith('/')) return url
  try {
    const u = new URL(url)
    const at = u.pathname.indexOf('/api/')
    return `${at >= 0 ? u.pathname.slice(at) : u.pathname}${u.search}`
  } catch {
    return url
  }
}

/** Reports a 401 answer to the session-end listener without reading the body;
 * the one reporting path for request() and every direct fetch call (#835). */
export function reportIfUnauthorized(res: Response, pathOrUrl: string): void {
  if (res.status !== 401) return
  try {
    unauthorizedListener?.(res.status, apiPathOf(pathOrUrl))
  } catch {
    // the caller still gets its own error
  }
}

/** Plain fetch for calls that cannot use request() (uploads, blobs, full URLs):
 * same Response, but a 401 reaches the central session end (#835). */
export async function apiFetch(url: string, init?: RequestInit): Promise<Response> {
  const res = await fetch(url, init)
  reportIfUnauthorized(res, url)
  return res
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  // Only set Content-Type when we actually send a body — Fastify rejects
  // POST/PATCH with `Content-Type: application/json` and an empty body (FST_ERR_CTP_EMPTY_JSON_BODY).
  const hasBody = init.body != null
  const headers: Record<string, string> = { ...((init.headers as Record<string, string>) ?? {}) }
  if (hasBody && !headers['Content-Type']) headers['Content-Type'] = 'application/json'

  const res = await fetch(`${BASE_URL}${path}`, {
    credentials: 'include',
    ...init,
    headers,
  })

  if (!res.ok) {
    reportIfUnauthorized(res, path)
    let message = `Request failed (${res.status})`
    let code: string | undefined
    try {
      const data = (await res.json()) as { error?: string; code?: string }
      if (data?.error) message = data.error
      if (typeof data?.code === 'string') code = data.code
    } catch {
      /* response had no JSON body */
    }
    const err = new Error(message) as Error & { status: number; code?: string }
    err.status = res.status
    err.code = code
    throw err
  }

  if (res.status === 204) return undefined as T
  const ct = res.headers.get('content-type') ?? ''
  if (!ct.includes('application/json')) {
    return (await res.text()) as unknown as T
  }
  return (await res.json()) as T
}

// #1861 Coaching-Abo
export interface CoachingAboKontingent {
  nr: number
  von: string
  bis: string
  grund: number
  uebertrag: number
  zukauf: number
  gesamt: number
  verbraucht: number
  geplant: number
  genutzt: number
  rest: number
  ueberschreitung: number
  prozent: number
  warnung: number
}
export interface CoachingAboStufe {
  slug: string
  art: 'abo' | 'gruppe' | 'nachkauf'
  name: string
  beschreibung: string
  preisCent: number
  minuten: number
  maxTeilnehmer: number | null
  aktiv: boolean
  nettoJeStundeCent: number | null
  rechnung: string
}
export interface CoachingAboRegeln {
  einheitMin: number
  aufrunden: boolean
  uebertragMonate: 0 | 1
  uebertragMaxProzent: number
  ueberschreitung: 'sperren' | 'nachberechnen'
  zeitwertNettoCentJeMin: number
  stornoStunden: number
  warnSchwellen: number[]
  mindestlaufzeitMonate: number
  chatKurzMin: number
  chatLangMin: number
  terminDauern: number[]
  platzhalter: boolean
  konfiguriert: boolean
}
export interface CoachingAboUebersicht {
  bereich: { id: string; name: string; steuersatz: number | null } | null
  einstellungen?: CoachingAboRegeln
  stufen?: CoachingAboStufe[]
  abos?: {
    id: string
    name: string
    email: string
    kind: string
    company: string
    stufe: string
    art: 'abo' | 'gruppe'
    status: string
    preisCent: number
    minutenMonat: number
    mindestBis: string | null
    kuendigungZum: string | null
    wunschStufe: string | null
    kontingent: CoachingAboKontingent | null
    warnung: number
    hinweiseRechnung: number
  }[]
  gruppentermine?: {
    id: string
    titel: string
    start: string
    dauerMin: number
    maxTeilnehmer: number
    zugangUrl: string | null
    abgesagt: boolean
    belegt: number
  }[]
}

export interface RolePermissions {
  all?: boolean
  viewTeam?: boolean
  manageTeam?: boolean
  viewReports?: boolean
  manageCustomers?: boolean
  manageProjects?: boolean
  manageProducts?: boolean
  manageInvoices?: boolean
  manageMail?: boolean
  manageDocuments?: boolean
  manageFetcher?: boolean
  manageTasks?: boolean
  manageTickets?: boolean
  manageShifts?: boolean
  manageMeetings?: boolean
  manageWorkflows?: boolean
  manageSettings?: boolean
  manageLogbook?: boolean
  viewDashboardOverview?: boolean
}

export interface Role {
  id: string
  name: string
  description: string | null
  isSystem: boolean
  isAdmin: boolean
  sidebarVisibility: Record<string, boolean> | null
  permissions: RolePermissions | null
  widgetVisibility: Record<string, boolean> | null
  createdAt: string
  updatedAt: string
  userCount?: number
}

export interface User {
  id: string
  email: string
  name: string
  role: 'admin' | 'user'
  roleId: string | null
  hourlyRateCents: number | null
  archived: boolean
  nonbusinessgroupId: string | null
  breakRuleId: string | null
  editLockMode: 'none' | 'dynamic' | 'static' | 'full'
  editLockDynDays: number | null
  editLockUntil: string | null
  sidebarVisibility: Record<string, boolean> | null
  sidebarCollapsed: boolean
  supervisorId: string | null
  supervisorName?: string | null
  personnelNumber: string | null
  startPage: string | null
  createdAt: string
  /** Full role object, populated by /auth/me */
  roleObj?: Role | null
}

export interface DashboardTemplate {
  id: string
  name: string
  description: string | null
  layout: unknown
  createdBy: string | null
  createdAt: string
  updatedAt: string
  /** Number of users currently assigned to this template (admin list only). */
  assignedCount?: number
}

export interface ManageableUser {
  id: string
  name: string
  email: string
  role: 'admin' | 'user'
  nonbusinessgroupId: string | null
  breakRuleId: string | null
}

export interface Nonbusinessgroup {
  id: string
  name: string
  country: string
  region: string | null
  isBuiltin: boolean
}

export interface Nonbusinessday {
  id: string
  nonbusinessgroupId: string
  date: string
  name: string
  halfDay: boolean
}

export interface BreakRuleTier {
  afterHours: number
  requiredMinutes: number
}

export interface BreakRule {
  id: string
  name: string
  country: string
  region: string | null
  tiers: BreakRuleTier[]
  isBuiltin: boolean
  createdAt: string
  updatedAt: string
}

export interface Customer {
  version: number
  id: string
  name: string
  notes: string | null
  archived: boolean
  /** When true, only admins + explicit grantees see/edit this record. */
  restricted: boolean
  createdBy: string | null
  billingContactName: string | null
  billingEmail: string | null
  billingStreet: string | null
  billingZip: string | null
  billingCity: string | null
  billingCountry: string | null
  billingVatId: string | null
  buyerReference: string | null
  /** Stabile Kundennummer = Schlüssel zum emberfibu-Kontakt (#1007). */
  kundennummer: string
  createdAt: string
  updatedAt: string
  /** Computed per-request: true if current user may PATCH this row. */
  _canEdit?: boolean
}

export interface InvoiceSettings {
  id: string
  issuerName: string
  issuerStreet: string | null
  issuerZip: string | null
  issuerCity: string | null
  issuerCountry: string
  issuerEmail: string | null
  issuerPhone: string | null
  issuerWebsite: string | null
  taxId: string | null
  vatId: string | null
  commercialRegister: string | null
  managingDirector: string | null
  bankName: string | null
  bankIban: string | null
  bankBic: string | null
  defaultPaymentTermsDays: number
  defaultTaxRatePercent: string
  defaultCurrency: string
  invoiceNumberFormat: string
  accentColor: string
  footerText: string | null
  smallBusinessClause: boolean
  logoDataUrl: string | null
  // Layout-Designer (Migration 0010 + 0012)
  layoutTemplate: 'classic' | 'modern' | 'minimal' | 'bold' | 'corporate' | 'professional'
  fontFamily: 'helvetica' | 'times' | 'courier'
  baseFontSize: number
  headerStyle: 'plain' | 'band' | 'sidebar' | 'boxed' | 'professional'
  headerBgColor: string | null
  headerTextColor: string | null
  logoPosition: 'left' | 'right' | 'center' | 'hidden'
  logoMaxHeight: number
  tagline: string | null
  tableZebra: boolean
  tableHeaderStyle: 'filled' | 'outlined' | 'minimal'
  showLineNumbers: boolean
  showLineTax: boolean
  totalsBoxStyle: 'plain' | 'outlined' | 'filled'
  marginPreset: 'compact' | 'normal' | 'generous'
  pageNumbers: boolean
  watermarkText: string | null
  watermarkOpacity: string
  greetingTemplate: string
  closingTemplate: string
  signatureDataUrl: string | null
  footerColumns: number
  dateFormat: 'de' | 'iso' | 'us' | 'long'
  // Migration 0012 — IT, Finance & Flow / clockodo-style fields
  senderLine: string | null
  currencyDisplay: 'code' | 'symbol'
  showArticleNumber: boolean
  subtitleNetto: boolean
  totalsLabelTotal: string
  totalsLabelSubtotal: string
  headerMetaLayout: 'right-block' | 'split-lr'
  receiverLabel: string
  // Migration 0013
  court: string | null
  updatedAt: string
}

export interface Project {
  version: number
  id: string
  customerId: string
  name: string
  description: string | null
  startDate: string | null
  endDate: string | null
  hourlyRateCents: number | null
  budgetHours: string | null
  color: string | null
  archived: boolean
  restricted: boolean
  createdAt: string
  updatedAt: string
  _canEdit?: boolean
}

export interface Service {
  id: string
  name: string
  defaultHourlyRateCents: number | null
  billable: boolean
  archived: boolean
  restricted: boolean
  createdAt: string
  updatedAt: string
  _canEdit?: boolean
}

export interface Product {
  id: string
  name: string
  description: string | null
  defaultPriceCents: number
  unit: string
  defaultTaxRatePercent: string | null
  archived: boolean
  restricted: boolean
  createdAt: string
  updatedAt: string
  _canEdit?: boolean
}

/** Per-record access control payload (Stammdaten). */
export type EntityResourceType = 'customer' | 'project' | 'product' | 'service' | 'logbook_vehicle'

export interface LogbookVehicle {
  id: string
  name: string
  licensePlate: string | null
  archived: boolean
  restricted: boolean
  createdAt: string
  updatedAt: string
  _canEdit?: boolean
}

export interface LogbookTrip {
  id: string
  vehicleId: string
  tripDate: string
  /** Manuelle Reihenfolge (Drag&Drop), 0 = vorn; nur via Reorder-Endpoint. */
  position: number
  startTime: string | null
  endTime: string | null
  route: string | null
  purpose: string | null
  contacts: string | null
  startKm: number | null
  kmBusiness: number
  kmCommute: number
  kmPrivate: number
  endKm: number | null
  fuelLiters: string | null
  fuelCostCents: number | null
  miscCostCents: number | null
  driverName: string | null
  notes: string | null
  createdAt: string
  updatedAt: string
}

export type LogbookTripInput = Partial<
  Omit<LogbookTrip, 'id' | 'vehicleId' | 'position' | 'createdAt' | 'updatedAt' | 'fuelLiters'>
> & {
  fuelLiters?: number | null
}
export type EntitySubjectType = 'user' | 'team'
export type EntityPermission = 'view' | 'edit'

export interface EntityGrant {
  subjectType: EntitySubjectType
  subjectId: string
  subjectName?: string | null
  permission: EntityPermission
}

export interface EntityAccess {
  restricted: boolean
  grants: EntityGrant[]
}

export interface EntityGroupSummary {
  id: string
  resourceType: EntityResourceType
  name: string
  color: string | null
  sortOrder: number
  createdBy: string | null
  createdAt: string
  updatedAt: string
  memberIds: string[]
}

export type WorkflowScheduleKind = 'manual' | 'daily' | 'weekly' | 'monthly' | 'yearly'

export interface WorkflowScheduleConfig {
  time?: string
  weekday?: number
  dayOfMonth?: number
  month?: number
}

export type WorkflowActionType = 'create_invoice_from_time_entries'

export type WorkflowPeriodStrategy =
  | 'last_week'
  | 'last_month'
  | 'last_quarter'
  | 'last_year'
  | 'current_month'
  | 'current_year'

export interface CreateInvoiceFromEntriesConfig {
  customerId: string
  projectIds?: string[]
  userIds?: string[]
  period: WorkflowPeriodStrategy
  billableOnly?: boolean
  excludeAlreadyBilled?: boolean
  taxRatePercent?: number
}

export interface Workflow {
  id: string
  name: string
  description: string | null
  schedule: WorkflowScheduleKind
  scheduleConfig: WorkflowScheduleConfig
  actionType: WorkflowActionType
  actionConfig: Record<string, unknown>
  enabled: boolean
  nextRunAt: string | null
  lastRunAt: string | null
  lastRunStatus: string | null
  lastRunError: string | null
  createdBy: string | null
  createdAt: string
  updatedAt: string
}

export interface WorkflowRun {
  id: string
  workflowId: string
  startedAt: string
  completedAt: string | null
  status: 'running' | 'success' | 'failed' | 'skipped'
  triggeredBy: 'schedule' | 'manual'
  result: Record<string, unknown> | null
  error: string | null
}

export interface ChatListItem {
  id: string
  name: string | null
  isGroup: boolean
  /** Projekt-Zuordnung (Hub Paket 4) — reine Anzeige, kein Gate. */
  projectId: string | null
  createdAt: string
  members: Array<{ id: string; name: string; email: string }>
  lastMessage: {
    id: string
    content: string
    kind: ChatMessageKind
    senderUserId: string
    createdAt: string
  } | null
  unread: number
}

export type ChatMessageKind = 'text' | 'file' | 'link' | 'voice' | 'absence_request' | 'system'

export interface ChatAttachment {
  documentId: string
  filename: string
  mimeType: string
  size: number
  /** 'copy' = personal upload owned by sender; 'link' = pointer to a
   *  pre-existing document the sender has access to + grants the recipient. */
  kind: 'copy' | 'link'
  thumbnailUrl?: string | null
  projectId?: string | null
  /** Nur für Sprachnachrichten: Aufnahmedauer in Millisekunden. */
  durationMs?: number | null
}

export interface ChatMessage {
  id: string
  chatId: string
  senderUserId: string
  content: string
  kind: ChatMessageKind
  attachments: ChatAttachment[]
  referenceId: string | null
  /** Nur für kind='voice': asynchrones Transkript + Status. */
  transcript?: string | null
  transcriptStatus?: 'pending' | 'done' | 'failed' | 'disabled' | null
  createdAt: string
}

export interface DocumentShare {
  id: string
  documentId: string
  sharedWithUserId: string
  sharedByUserId: string | null
  permission: 'view' | 'edit'
  source: 'chat' | 'manual'
  sourceChatMessageId: string | null
  createdAt: string
}

export type RsvpStatus = 'invited' | 'accepted' | 'declined'

export interface MeetingParticipant {
  userId: string
  name: string
  email: string
  status: RsvpStatus
}

export interface Meeting {
  id: string
  title: string
  description: string | null
  startTime: string
  endTime: string
  onlineUrl: string | null
  /** Set when the meeting is Jitsi-managed; participants get a JWT
   * join-URL via /meetings/:id/join. Null = BYO URL. */
  roomSlug: string | null
  location: string | null
  /** Zählt als Arbeitszeit (Ist) — für alle Teilnehmer; Entscheid des Erstellers. */
  countsAsWork: boolean
  createdBy: string | null
  createdAt: string
  updatedAt: string
  participants: MeetingParticipant[]
}

/** #751: guest link as listed for admins — never contains the token. */
export interface MeetingGuestLink {
  id: string
  createdAt: string
  expiresAt: string
  usedAt: string | null
  revokedAt: string | null
}

/** Freshly issued guest link — `url` carries the plaintext token in its
 * fragment and is only ever returned once. */
export interface IssuedGuestLink {
  id: string
  url: string
  expiresAt: string
  createdAt: string
}

export type GuestPreview =
  | {
      state: 'ok'
      hostFirstName: string
      startTime: string
      endTime: string
      expiresAt: string
      rejoin: boolean
    }
  | { state: 'used' }
  | { state: 'unavailable' }
  | { state: 'slow_down' }
  | { state: 'error' }

export type GuestJoin =
  | { state: 'ok'; joinUrl: string }
  | { state: 'used' }
  | { state: 'unavailable' }
  | { state: 'invalid_name' }
  | { state: 'slow_down' }
  | { state: 'error' }

/** Guest endpoints answer with a `state` on every status; the token only
 * travels in the POST body (never path/query) and the response is not
 * cached. */
async function guestPost(path: string, body: unknown): Promise<Record<string, unknown>> {
  try {
    const res = await fetch(`${BASE_URL}${path}`, {
      method: 'POST',
      credentials: 'include',
      cache: 'no-store',
      referrerPolicy: 'no-referrer',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    const data = (await res.json().catch(() => null)) as Record<string, unknown> | null
    if (data && typeof data.state === 'string') return data
    if (res.ok && data && typeof data.joinUrl === 'string') return { state: 'ok', ...data }
    return { state: 'error' }
  } catch {
    return { state: 'error' }
  }
}

/** Compact form returned by /meetings/recent — no participants. */
export interface MeetingSummary {
  id: string
  title: string
  description: string | null
  startTime: string
  endTime: string
  onlineUrl: string | null
  roomSlug: string | null
  location: string | null
  countsAsWork: boolean
  createdBy: string | null
  createdAt: string
  updatedAt: string
}

export interface AppSettings {
  meetingBaseUrl: string | null
  meetingJwtAppId: string
  /** Server tells the client whether the secret is initialized — never
   * the value itself. */
  meetingJwtSecretSet: boolean
  /** Microsoft OAuth app credentials for mail. Admin configures once
   * in Settings, every user can then OAuth their own mailbox against
   * the registered Azure AD app. The client_secret is never returned;
   * the boolean only tells the UI whether it's set. */
  oauthMicrosoftClientId: string | null
  oauthMicrosoftTenant: string
  oauthMicrosoftSecretSet: boolean
  /** Fastbill draft-push integration. Email + project id are returned as-is;
   * the API key is never returned — the boolean only says whether it's set. */
  fastbillEmail: string | null
  fastbillProjectId: string | null
  fastbillApiKeySet: boolean
  updatedAt: string
}

export interface AiConfig {
  provider: string
  model: string | null
  configured: boolean
  stt: { provider: string; model: string | null; configured: boolean }
}

export interface AiEvent {
  type: 'text' | 'tool_call' | 'tool_result' | 'done'
  content?: string
  name?: string
  arguments?: Record<string, unknown>
  success?: boolean
  summary?: string
}

export interface AiChatResponse {
  events: AiEvent[]
}

export interface MailAccount {
  protected: boolean
  tlsServername: string | null
  disabledAt: string | null
  id: string
  userId: string
  label: string
  email: string
  authType: string
  imapHost: string
  imapPort: number
  imapSecure: boolean
  imapUser: string
  smtpHost: string
  smtpPort: number
  smtpSecure: boolean
  smtpUser: string
  fromName: string | null
  createdAt: string
  updatedAt: string
  lastSyncAt: string | null
  lastSyncError: string | null
  /** New-mail push for this account (#775) and what it shows (#774). */
  pushEnabled: boolean
  pushContent: MailPushContent
}

export interface MailFolder {
  protected: boolean
  id: string
  accountId: string
  path: string
  name: string
  delimiter: string | null
  flags: string[]
  unreadCount: number
  totalCount: number
}

export interface MailMessage {
  id: string
  accountId: string
  folderPath: string
  uid: number
  messageId: string | null
  inReplyTo: string | null
  threadRoot: string | null
  fromAddr: string | null
  toAddrs: string | null
  ccAddrs: string | null
  subject: string | null
  internalDate: string | null
  bodyText: string | null
  bodyHtml: string | null
  flags: string[]
  sizeBytes: number | null
  fetchedAt: string
  /** Set when the mail came back from „Später“ (#780); absent on older servers. */
  resurfacedAt?: string | null
}

export interface MailConversation {
  threadRoot: string
  messages: MailMessage[]
  latest: MailMessage
  unreadCount: number
}

/** A conversation of the unified inbox (#535): never spans two accounts.
 * `protected` covers the account and its INBOX (missing folder row = true). */
export interface MailUnifiedConversation extends MailConversation {
  accountId: string
  protected: boolean
}

export interface MailAttachment {
  id: string
  messageId: string
  filename: string
  contentType: string
  sizeBytes: number
  contentId: string | null
  contentDisposition: string | null
  createdAt: string
}

export interface SendMailAttachment {
  filename: string
  contentType: string
  contentBase64: string
  sizeBytes?: number
  cid?: string
  contentDisposition?: 'inline' | 'attachment'
  /** Stored only in drafts/outbox; never trusted by the send endpoint. */
  sourceAttachmentId?: string
  draftPartId?: string
  missing?: boolean
}

export interface MailDraft {
  autoAttachSignature?: boolean
  id: string
  accountId: string
  toAddrs: string
  ccAddrs: string
  bccAddrs: string
  subject: string
  bodyText: string
  bodyHtml?: string
  forwardQuoteHtml?: string
  forwardQuoteText?: string
  forwardRemovedIds?: string[]
  inReplyTo: string | null
  references: string[]
  attachmentsJson: SendMailAttachment[] | unknown
  /** Mail a reply or forward is based on (#830); null when gone or none. */
  sourceMessageId?: string | null
  sourceAccountId?: string | null
  /** Only when loaded singly: the stored source is protected or gone (#830). */
  sourceProtected?: boolean
  /** Sender identity chosen in the compose dialog (#839); null = account default. */
  identityId?: string | null
  createdAt: string
  updatedAt: string
}

export interface Tag {
  id: string
  name: string
  color: string
  createdAt: string
}

export interface Task {
  id: string
  title: string
  description: string | null
  status: 'open' | 'in_progress' | 'done'
  priority: 'low' | 'normal' | 'high' | 'critical'
  dueDate: string | null
  assigneeId: string | null
  assigneeName: string | null
  createdBy: string
  creatorName: string | null
  projectId: string | null
  completedAt: string | null
  createdAt: string
  updatedAt: string
}

export interface TimeEntry {
  id: string
  userId: string
  projectId: string | null
  serviceId: string | null
  description: string
  notes: string | null
  startTime: string
  endTime: string | null
  travelTimeToMinutes: number
  travelTimeFromMinutes: number
  /** Zählt die Reisezeit dieses Eintrags als Arbeitszeit (Ist)? */
  travelCountsAsWork: boolean
  meetingId: string | null
  billable: boolean
  durationMinutes: number | null
  createdAt: string
  updatedAt: string
  tagIds?: string[]
}

/** Clockodo-artiger Titel-Vorschlag: distinct Beschreibung aus dem eigenen
 * Verlauf mit Projekt/Tätigkeit der letzten Verwendung + Nutzungszähler. */
export interface EntrySuggestion {
  description: string
  projectId: string | null
  serviceId: string | null
  lastUsedAt: string
  count: number
}

export interface Break {
  id: string
  userId: string
  startTime: string
  endTime: string | null
  reason: string | null
  createdAt: string
}

export type AbsenceType =
  | 'vacation'
  | 'sick'
  | 'training'
  | 'other'
  | 'overtime_reduction'
  | 'maternity'
  | 'parental'

export type AbsenceStatus = 'pending' | 'approved' | 'rejected' | 'withdrawn' | 'cancelled'

export type AbsenceHalf = 'morning' | 'afternoon'

export interface Absence {
  id: string
  userId: string
  type: AbsenceType
  fromDate: string
  toDate: string
  notes: string | null
  /** Half-day flag on the first day. null = full day. Single-day absences only use this field. */
  firstDayHalf: AbsenceHalf | null
  /** Half-day flag on the last day. null = full day. Ignored when fromDate === toDate. */
  lastDayHalf: AbsenceHalf | null
  status: AbsenceStatus
  reviewedBy: string | null
  reviewedAt: string | null
  reviewNote: string | null
  /** Substitute (Vertretung) — colleague who covers while the user is out. */
  substituteUserId: string | null
  /** AU-Bescheinigung document id (sick absences). */
  sickNoteDocumentId: string | null
  /** Auto-set on the API when type='sick' and span >= 3 days (§5 EFZG). */
  sickNoteRequired: boolean
  createdAt: string
  /** Enriched by /absences/pending endpoint */
  userName?: string | null
  /** Enriched by /absences/all endpoint */
  userEmail?: string | null
  /** Enriched reviewer name */
  reviewerName?: string | null
  /** Cancellation workflow — true while a stornierungs-request is pending review. */
  cancellationRequested?: boolean
  cancellationReason?: string | null
  cancellationRequestedAt?: string | null
  cancellationApprovedBy?: string | null
}

export type BlackoutMode = 'warn' | 'block'

export interface VacationBlackout {
  id: string
  fromDate: string
  toDate: string
  name: string
  reason: string | null
  blockMode: BlackoutMode
  teamId: string | null
  createdBy: string | null
  createdAt: string
}

export interface VacationRules {
  vacationMinLeadDays: number
  vacationMaxConsecutiveDays: number
}

// Time Accounts (Stundenkonten)
export interface TimeAccountMonth {
  month: number
  targetMinutes: number
  actualMinutes: number
  diffMinutes: number
  balanceMinutes: number
  workDays: number
  absenceDays: number
}

export interface TimeAccountAdjustment {
  type: string
  date: string
  minutes: number
  description: string | null
}

export interface TimeAccountVacationAdjustment {
  id: string
  type: string
  year: number
  days: number
  description: string | null
  expiresAt: string | null
}

export interface TimeAccountData {
  userId: string
  userName: string
  year: number
  months: TimeAccountMonth[]
  totalTargetMinutes: number
  totalActualMinutes: number
  totalBalanceMinutes: number
  vacationDaysEntitled: number | null
  vacationDaysTaken: number
  vacationDaysPlanned: number
  vacationDaysRemaining: number | null
  vacationCarryoverDays: number
  vacationAdjustmentDays: number
  vacationAdjustments: TimeAccountVacationAdjustment[]
  adjustments: TimeAccountAdjustment[]
  carryoverMinutes: number
  /** Sum of approved sick-absence days YTD (half-days count as 0.5). */
  sickDaysYTD: number
  /** True when sickDaysYTD has crossed 30 of the 42-day Entgeltfortzahlung cap. */
  sickPayoutCapWarning: boolean
}

// Work Schedules
export interface WorkSchedule {
  id: string
  userId: string
  label: string | null
  validFrom: string
  validTo: string | null
  weeklyHours: string
  monHours: string
  tueHours: string
  wedHours: string
  thuHours: string
  friHours: string
  satHours: string
  sunHours: string
  createdAt: string
  updatedAt: string
}

// Hour Adjustments
export interface HourAdjustment {
  id: string
  userId: string
  type: 'carryover' | 'payout' | 'correction'
  date: string
  minutes: number
  description: string | null
  createdBy: string | null
  createdAt: string
}

// Vacation Adjustments (Resturlaubsübertrag etc)
export type VacationAdjustmentType = 'carryover' | 'payout' | 'expiry' | 'correction'

export interface VacationAdjustment {
  id: string
  userId: string
  type: VacationAdjustmentType
  year: number
  /** Numeric stored as string (Postgres numeric). */
  days: string
  description: string | null
  /** ISO date or null. For carryover, defaults to 31.03 of year+1. */
  expiresAt: string | null
  createdBy: string | null
  createdAt: string
}

// Vacation Entitlements (zeitraum-basierter Urlaubsanspruch)
export interface VacationEntitlement {
  id: string
  userId: string
  validFrom: string
  validTo: string | null
  /** Numeric stored as string (Postgres numeric). */
  daysPerYear: string
  notes: string | null
  createdAt: string
}

export interface PerformanceReport {
  totalMinutes: number
  billableMinutes: number
  revenueNetCents: number
  activities: Array<{ name: string; minutes: number; color: string }>
  users?: Array<{ id: string; name: string; totalMinutes: number; revenueNetCents: number }>
}

export interface TeamStatusReport {
  absent: Array<{ userId: string; userName: string; type: AbsenceType }>
  active: Array<{
    userId: string
    userName: string
    durationMs: number
    projectName: string | null
  }>
  available: Array<{ userId: string; userName: string }>
}

export interface ComplianceViolation {
  id: string
  userId: string
  userName: string
  date: string
  type: 'max_hours' | 'missing_break' | 'rest_period' | 'sunday_work'
  severity: 'warning' | 'error'
  description: string
  entryId: string | null
  details: {
    actual: number
    required: number
  }
}

export interface ComplianceReport {
  violations: ComplianceViolation[]
}

export interface TeamYearSummaryRow {
  userId: string
  name: string
  personnelNumber: string | null
  teamName: string | null
  /** YTD-Aggregat aller time_entries, sekundengenau. */
  actualSeconds: number
  /** YTD-Soll (Tagessoll pro Werktag bis heute, ohne Feiertage). */
  targetSeconds: number
  /** Saldo = actual + Abwesenheits-Credit − target. */
  balanceSeconds: number
  vacation: {
    /** Jahresanspruch (null wenn nicht konfiguriert). */
    entitled: number | null
    /** Genommene (approved) Werktage YTD. */
    taken: number
    planned: number
    /** Resturlaub = entitled + adjustments − taken − planned. */
    remaining: number | null
  }
  /** Approved Krankheits-Werktage YTD. */
  sickDays: number
  /** Approved 'training' + 'other' Werktage YTD. */
  otherAbsenceDays: number
}

export interface TeamYearSummary {
  year: number
  ytdEnd: string
  rows: TeamYearSummaryRow[]
}

export interface ImportPreset {
  id: string
  sourceFilename: string
  targetTable: string
  label: string | null
  mapping: Record<string, unknown>
  createdAt: string
  updatedAt: string
}

export interface DailyReport {
  date: string
  totalMs: number
  targetHours: number
  byProject: {
    projectId: string | null
    projectName: string | null
    durationMs: number
    entries: number
  }[]
}

export interface WeeklyReport {
  weekStart: string
  days: { date: string; weekday: number; actualMs: number; targetHours: number }[]
  weekActualMs: number
  weekTargetHours: number
}

export interface RangeReport {
  from: string | null
  to: string | null
  /** Summe aus Einträgen + Abwesenheits-Soll + nicht-erfassten Meetings */
  totalMs: number
  /** Pure time-entry duration. */
  entryMs: number
  /** Genehmigte Abwesenheits-Tage × Tages-Soll. */
  absenceMs: number
  /** Anzahl genehmigter Abwesenheits-Tage (halb-Tage = 0.5). */
  absenceDays: number
  /** Meeting-Zeit ohne überlappenden time_entry. */
  meetingMs: number
  entries: number
  byProject: {
    projectId: string | null
    projectName: string | null
    customerId: string | null
    customerName: string | null
    durationMs: number
    entries: number
    revenueCents: number | null
  }[]
}

export type ProjectPageBlockType = 'paragraph' | 'heading' | 'todo' | 'divider' | 'callout'

export interface ProjectPageBlock {
  id: string
  type: ProjectPageBlockType
  text?: string
  checked?: boolean
  level?: 1 | 2
}

export interface ProjectPageSummary {
  id: string
  projectId: string
  title: string
  position: number
  updatedBy: string | null
  createdAt: string
  updatedAt: string
}

export interface ProjectPage extends ProjectPageSummary {
  blocks: ProjectPageBlock[]
  createdBy: string | null
}

export interface ProjectActivityEntry {
  id: string
  action: string
  userId: string | null
  userName: string | null
  createdAt: string
  meta: { name?: string; files?: string[]; title?: string } | null
}

export interface DocumentSummary {
  id: string
  uploaderUserId: string
  filename: string
  mimeType: string
  storagePath: string
  sizeBytes: number
  pageCount: number | null
  projectId: string | null
  createdAt: string
  archivedAt: string | null
}

export type DocSignatureStatus = 'pending' | 'signed' | 'rejected'

export interface DocSignature {
  id: string
  documentId: string
  recipientUserId: string
  sentByUserId: string
  status: DocSignatureStatus
  message: string | null
  sentAt: string
  openedAt: string | null
  signedAt: string | null
  rejectedAt: string | null
  rejectionReason: string | null
  signedStoragePath: string | null
  signatureDate: string | null
  signatureLocation: string | null
  signatureImage: string | null
}

export interface DocAuditEntry {
  id: string
  documentId: string
  signatureId: string | null
  userId: string | null
  action: string
  ip: string | null
  meta: Record<string, unknown> | null
  createdAt: string
}

// Audit log (Verlauf)
export interface AuditEntry {
  id: string
  userId: string | null
  userName: string | null
  userEmail: string | null
  action: string
  resource: string
  resourceId: string | null
  before: unknown
  after: unknown
  ip: string | null
  createdAt: string
  revertedAt: string | null
  revertedByUserId: string | null
  revertedByAuditId: string | null
}

// Tickets
export type TicketType = 'bug' | 'feature' | 'improvement'
export type TicketPriority = 'low' | 'normal' | 'high' | 'critical'

export interface Ticket {
  id: string
  title: string
  description: string
  type: TicketType
  status: string
  priority: TicketPriority
  columnId: string
  columnOrder: number
  createdBy: string
  assignedTo: string | null
  createdAt: string
  updatedAt: string
  /** Joined creator name (present in the /tickets list response). */
  creatorName?: string | null
}

export interface TicketDetail {
  ticket: Ticket
  attachments: TicketAttachment[]
  comments: TicketCommentWithUser[]
  creatorName: string | null
  assigneeName: string | null
}

export interface TicketAttachment {
  id: string
  ticketId: string
  filename: string
  mimeType: string
  sizeBytes: number
  storagePath: string
  createdAt: string
}

export interface TicketComment {
  id: string
  ticketId: string
  userId: string
  content: string
  createdAt: string
}

export interface TicketCommentWithUser extends TicketComment {
  userName: string | null
}

export interface KanbanColumn {
  id: string
  name: string
  color: string | null
  position: number
  isDefault: boolean
  createdAt: string
}

export interface BoardTicket extends Ticket {
  creatorName: string | null
  attachmentCount: number
  commentCount: number
}

export interface BoardData {
  columns: KanbanColumn[]
  tickets: BoardTicket[]
}

// Shifts
export interface ShiftType {
  id: string
  name: string
  shortName: string
  color: string
  startTime: string
  endTime: string
  breakMinutes: number
  archived: boolean
  createdAt: string
}

export interface ShiftAssignment {
  id: string
  userId: string
  shiftTypeId: string | null
  date: string
  customStart: string | null
  customEnd: string | null
  notes: string | null
  createdBy: string
  createdAt: string
  updatedAt: string
  userName: string | null
  shiftTypeName: string | null
  shiftTypeShortName: string | null
  shiftTypeColor: string | null
}

// Inventory
export type InventoryStatus = 'available' | 'in-use' | 'maintenance' | 'defect'
export type InventoryCondition = 'good' | 'fair' | 'poor'

export interface InventoryCategory {
  id: string
  name: string
  icon: string | null
  color: string | null
  createdAt: string
}

export interface InventoryItem {
  id: string
  name: string
  description: string | null
  categoryId: string | null
  serialNumber: string | null
  qrCode: string | null
  status: InventoryStatus
  condition: InventoryCondition
  location: string | null
  responsibleUserId: string | null
  purchaseDate: string | null
  purchasePriceCents: number | null
  warrantyUntil: string | null
  maintenanceIntervalDays: number | null
  nextMaintenance: string | null
  insuranceInfo: string | null
  depreciationYears: number | null
  imageUrl: string | null
  archived: boolean
  createdAt: string
  updatedAt: string
  categoryName: string | null
  categoryIcon: string | null
  categoryColor: string | null
  responsibleUserName: string | null
}

export interface InventoryMaintenanceEntry {
  id: string
  itemId: string
  performedBy: string | null
  type: string
  description: string
  costCents: number | null
  performedAt: string
  createdAt: string
  performedByName: string | null
}

export interface InventoryLocationEntry {
  id: string
  itemId: string
  fromLocation: string | null
  toLocation: string
  movedBy: string | null
  movedAt: string
  movedByName: string | null
}

// Fetcher (invoice auto-download)
export type FetcherAccountStatus = 'active' | 'syncing' | 'awaiting-2fa' | 'error'
export type FetchedDocumentStatus = 'new' | 'reviewed' | 'booked'
export type FetchedDocumentType = 'invoice' | 'credit_note'

export interface FetcherAccount {
  id: string
  provider: string
  label: string
  email: string
  hasTotp: boolean
  syncFromDate: string | null
  status: FetcherAccountStatus
  lastSyncAt: string | null
  lastSyncError: string | null
  lastSyncCount: number | null
  createdBy: string
  createdAt: string
  updatedAt: string
}

export interface FetcherProvider {
  id: string
  name: string
  icon: string
  configSchema: { email: boolean; password: boolean; totp: boolean }
}

export interface FetchedDocument {
  id: string
  accountId: string
  provider: string
  externalId: string
  type: FetchedDocumentType
  documentNumber: string | null
  documentDate: string | null
  amountCents: number | null
  amountNetCents: number | null
  taxAmountCents: number | null
  taxRate: string | null
  currency: string | null
  vendorName: string | null
  filename: string
  mimeType: string
  storagePath: string
  sizeBytes: number
  status: FetchedDocumentStatus
  createdAt: string
}

// Disposition
export type DispositionStatus = 'planned' | 'confirmed' | 'active' | 'completed' | 'cancelled'

export interface DispositionEntry {
  id: string
  title: string
  projectId: string | null
  userId: string | null
  inventoryItemId: string | null
  startTime: string
  endTime: string
  location: string | null
  notes: string | null
  status: DispositionStatus
  color: string | null
  createdBy: string
  createdAt: string
  updatedAt: string
  userName: string | null
  inventoryItemName: string | null
  projectName: string | null
}

// Invoices
export type InvoiceStatus = 'draft' | 'sent' | 'paid' | 'cancelled'

export interface InvoiceListItem {
  id: string
  invoiceNumber: string
  customerId: string
  customerName: string | null
  status: InvoiceStatus
  issueDate: string
  dueDate: string | null
  periodFrom: string | null
  periodTo: string | null
  subtotalCents: number
  taxRatePercent: string
  taxCents: number
  totalCents: number
  currency: string
  notes: string | null
  createdBy: string | null
  /** Fastbill Ansatz B: set once this invoice has been pushed to Fastbill. */
  fastbillId: string | null
  fastbillSyncedAt: string | null
  createdAt: string
  updatedAt: string
}

export interface InvoiceLineItem {
  id: string
  invoiceId: string
  timeEntryId: string | null
  productId: string | null
  userId: string | null
  projectId: string | null
  description: string
  quantity: string // numeric — server returns as string
  unit: string
  unitPriceCents: number
  totalCents: number
  /** Per-line VAT rate as numeric string ("19.00"). Null = inherit invoice default. */
  taxRatePercent: string | null
  position: number
  createdAt: string
}

export interface InvoiceDetail extends InvoiceListItem {
  lineItems: InvoiceLineItem[]
}

export interface BuilderEntry {
  id: string
  userId: string
  userName: string | null
  projectId: string | null
  projectName: string | null
  customerId: string | null
  customerName: string | null
  description: string
  startTime: string
  endTime: string | null
  durationMinutes: number
  suggestedRateCents: number | null
}

export interface InvoicePreviewLine {
  timeEntryId: string | null
  /** Set when the line comes from Stammdaten-Produkte (mutually exclusive
   * with timeEntryId in practice, but no constraint enforces it). */
  productId?: string | null
  description: string
  quantity: number
  unit: string
  unitPriceCents: number
  totalCents: number
  userId?: string | null
  projectId?: string | null
}

export interface InvoicePreview {
  lineItems: InvoicePreviewLine[]
  subtotalCents: number
  taxCents: number
  totalCents: number
  taxRatePercent: number
}

export interface CreateInvoicePayload {
  invoiceNumber?: string
  customerId: string
  issueDate?: string
  dueDate?: string | null
  periodFrom?: string | null
  periodTo?: string | null
  notes?: string | null
  taxRatePercent?: number
  currency?: string
  lineItems: Array<{
    timeEntryId?: string | null
    productId?: string | null
    description: string
    quantity: number
    unit?: string
    unitPriceCents: number
    taxRatePercent?: number
    userId?: string | null
    projectId?: string | null
  }>
}

export interface UpdateInvoicePayload {
  invoiceNumber?: string
  status?: InvoiceStatus
  customerId?: string
  issueDate?: string
  dueDate?: string | null
  periodFrom?: string | null
  periodTo?: string | null
  notes?: string | null
  taxRatePercent?: number
  currency?: string
}

export interface AddLineItemPayload {
  description: string
  quantity: number
  unit?: string
  unitPriceCents: number
  taxRatePercent?: number
  timeEntryId?: string | null
  productId?: string | null
  userId?: string | null
  projectId?: string | null
}

export interface UpdateLineItemPayload {
  description?: string
  quantity?: number
  unit?: string
  unitPriceCents?: number
  taxRatePercent?: number
  position?: number
  productId?: string | null
}

// === emberfibu-Abrechnung (#1007) ===
export type FibuModus = 'aus' | 'an' | 'exklusiv'
export interface FibuFirma {
  id: string
  name: string
  farbe: string | null
  steuersatzProzent: number
  standard: boolean
}
export interface FibuStatus {
  modus: FibuModus
  bereit: boolean
  webUrl: string | null
  firmen: FibuFirma[]
}
export type FibuUebergabeStatus =
  | 'offen'
  | 'in_abrechnung'
  | 'abgerechnet'
  | 'storniert'
  | 'freigegeben'
export interface FibuUebergabe {
  id: string
  status: FibuUebergabeStatus
  firma: { id: string; name: string; farbe: string | null }
  customerId: string
  kundennummer: string
  zeiten: number
  positionen: number
  nettoCent: number | null
  ustCent: number | null
  bruttoCent: number | null
  fibuRechnungId: string | null
  fibuStatus: string | null
  fibuNummer: string | null
  leistungVon: string
  leistungBis: string
  festgeschriebenAm: string | null
  letzterAbgleich: string | null
  createdAt: string
  oeffnenUrl: string | null
}
export interface FibuVorschauPosition {
  bezeichnung: string
  projektId: string | null
  projektName: string
  minuten: number
  mengeTausendstel: number
  einzelpreisNettoCent: number
  nettoCent: number
}
export interface FibuVorschau {
  firma: FibuFirma
  kunde: { id: string; name: string; kundennummer: string }
  zeiten: number
  minuten: number
  positionen: FibuVorschauPosition[]
  steuersatzProzent: number
  nettoCent: number
  ustCent: number
  bruttoCent: number
  leistungszeitraum: { von: string; bis: string }
  idempotenzSchluessel: string
}
export interface FibuAuswahl {
  customerId: string
  firmaId: string
  entryIds: string[]
  von?: string | null
  bis?: string | null
}
export interface FibuZielVorschlag {
  firmaId: string | null
  quelle: 'projekt' | 'kunde' | 'standard' | null
  gemerkt: boolean
}

export const api = {
  // Auth
  // Robuster Auth-Bootstrap: ein transienter Fehler (Cold-Start, Deploy-Moment,
  // kurzer Netzwerk-Hiccup) darf NICHT als "ausgeloggt" gewertet werden — sonst
  // wirft der Route-Guard fälschlich auf /login, obwohl die Session gültig ist.
  // Nur ein echtes 4xx (v.a. 401) ist eine definitive "nicht eingeloggt"-Antwort;
  // 5xx/Netzwerkfehler werden mehrfach retryt, bevor null zurückkommt.
  me: async (): Promise<{ user: User } | null> => {
    const maxAttempts = 4
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        return await request<{ user: User }>('/api/auth/me')
      } catch (err) {
        const status = (err as { status?: number }).status
        if (typeof status === 'number' && status >= 400 && status < 500) return null
        if (attempt === maxAttempts) return null
        // Backoff mit Jitter — vermeidet Thundering-Herd, wenn viele Tabs nach
        // einem kurzen Server-Ausfall gleichzeitig retryen.
        await new Promise((resolve) => setTimeout(resolve, 150 * attempt + Math.random() * 120))
      }
    }
    return null
  },
  login: (email: string, password: string) =>
    request<{ user: User }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  register: (email: string, name: string, password: string) =>
    request<{ user: User }>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, name, password }),
    }),
  logout: () =>
    request<{ success: boolean; autoStoppedEntryId: string | null }>('/api/auth/logout', {
      method: 'POST',
    }),
  usersCount: () => request<{ count: number }>('/api/users/count'),

  // Roles (custom roles system)
  listRoles: () => request<{ roles: Role[] }>('/api/roles'),
  createRole: (data: {
    name: string
    description?: string | null
    isAdmin?: boolean
    sidebarVisibility?: Record<string, boolean> | null
    permissions?: RolePermissions | null
    widgetVisibility?: Record<string, boolean> | null
  }) => request<{ role: Role }>('/api/roles', { method: 'POST', body: JSON.stringify(data) }),
  updateRole: (
    id: string,
    data: Partial<{
      name: string
      description: string | null
      isAdmin: boolean
      sidebarVisibility: Record<string, boolean> | null
      permissions: RolePermissions | null
      widgetVisibility: Record<string, boolean> | null
    }>,
  ) => request<{ role: Role }>(`/api/roles/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteRole: (id: string) =>
    request<{ success: boolean }>(`/api/roles/${id}`, { method: 'DELETE' }),

  // Profile (current user)
  updateProfile: (data: {
    name?: string
    email?: string
    nonbusinessgroupId?: string | null
    breakRuleId?: string | null
    sidebarVisibility?: Record<string, boolean> | null
    sidebarCollapsed?: boolean
    startPage?: string | null
    currentPassword?: string
    newPassword?: string
  }) =>
    request<{ user: User }>('/api/profile', {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  // User-management (admin)
  listUsers: (includeArchived = false) =>
    request<{ users: User[] }>(`/api/users${includeArchived ? '?archived=true' : ''}`),
  /** Slim user list scoped to what the current user can manage (own + team
   * members for leaders, everyone for admin). Used by view-as pickers. */
  listManageableUsers: () => request<{ users: ManageableUser[] }>('/api/users/manageable'),

  // === Dashboard layouts + templates ===
  fetchPageTitle: (url: string) =>
    request<{ title: string | null }>(`/api/dashboard/page-title?url=${encodeURIComponent(url)}`),
  getDashboardLayout: () =>
    request<{ layout: unknown; source: 'user' | 'template' | 'empty'; templateName?: string }>(
      '/api/dashboard/layout',
    ),
  saveDashboardLayout: (layout: unknown) =>
    request<{ success: boolean }>('/api/dashboard/layout', {
      method: 'PUT',
      body: JSON.stringify({ layout }),
    }),
  listDashboardTemplates: () =>
    request<{ templates: DashboardTemplate[] }>('/api/dashboard-templates'),
  createDashboardTemplate: (body: { name: string; description?: string; layout: unknown }) =>
    request<{ template: DashboardTemplate }>('/api/dashboard-templates', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateDashboardTemplate: (
    id: string,
    body: Partial<{ name: string; description: string | null; layout: unknown }>,
  ) =>
    request<{ template: DashboardTemplate }>(`/api/dashboard-templates/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteDashboardTemplate: (id: string) =>
    request<{ success: boolean }>(`/api/dashboard-templates/${id}`, { method: 'DELETE' }),
  assignDashboardTemplate: (id: string, userIds: string[], pushLayout: boolean) =>
    request<{ success: boolean; count: number; pushed: boolean }>(
      `/api/dashboard-templates/${id}/assign`,
      { method: 'POST', body: JSON.stringify({ userIds, pushLayout }) },
    ),
  unassignDashboardTemplate: (userIds: string[]) =>
    request<{ success: boolean }>('/api/dashboard-templates/unassign', {
      method: 'POST',
      body: JSON.stringify({ userIds }),
    }),
  createUser: (data: {
    email: string
    name: string
    password: string
    role?: 'admin' | 'user'
    roleId?: string | null
    hourlyRateCents?: number | null
    personnelNumber?: string | null
  }) => request<{ user: User }>('/api/users', { method: 'POST', body: JSON.stringify(data) }),
  updateUser: (
    id: string,
    data: Partial<{
      name: string
      email: string
      role: 'admin' | 'user'
      roleId: string | null
      hourlyRateCents: number | null
      password: string
      archived: boolean
      editLockMode: 'none' | 'dynamic' | 'static' | 'full'
      editLockDynDays: number | null
      editLockUntil: string | null
      sidebarVisibility: Record<string, boolean> | null
      supervisorId: string | null
      personnelNumber: string | null
    }>,
  ) => request<{ user: User }>(`/api/users/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteUser: (id: string) => request<{ user: User }>(`/api/users/${id}`, { method: 'DELETE' }),

  // Customers
  listCustomers: (includeArchived = false) =>
    request<{ customers: Customer[] }>(`/api/customers${includeArchived ? '?archived=true' : ''}`),
  getCustomer: (id: string) => request<{ customer: Customer }>(`/api/customers/${id}`),
  createCustomer: (
    data: {
      name: string
      notes?: string | null
      billingContactName?: string | null
      billingEmail?: string | null
      billingStreet?: string | null
      billingZip?: string | null
      billingCity?: string | null
      billingCountry?: string | null
      billingVatId?: string | null
      buyerReference?: string | null
      kundennummer?: string | null
    } & Pick<EntitySaveOptions, 'firmaId' | 'baseVersion'>,
    idempotencyKey?: string,
    revision?: number,
  ) =>
    request<{ customer: Customer }>('/api/customers', {
      method: 'POST',
      ...(idempotencyKey
        ? {
            headers: {
              'Idempotency-Key': idempotencyKey,
              ...(revision !== undefined ? { 'Creation-Revision': String(revision) } : {}),
            },
          }
        : {}),
      body: JSON.stringify(data),
    }),
  updateCustomer: (
    id: string,
    data: Partial<{
      name: string
      notes: string | null
      archived: boolean
      billingContactName: string | null
      billingEmail: string | null
      billingStreet: string | null
      billingZip: string | null
      billingCity: string | null
      billingCountry: string | null
      billingVatId: string | null
      buyerReference: string | null
      kundennummer: string
    }> &
      Pick<EntitySaveOptions, 'firmaId' | 'baseVersion'>,
    idempotencyKey?: string,
    revision?: number,
  ) =>
    request<{ customer: Customer }>(`/api/customers/${id}`, {
      method: 'PATCH',
      ...(idempotencyKey
        ? {
            headers: {
              'Idempotency-Key': idempotencyKey,
              ...(revision !== undefined ? { 'Creation-Revision': String(revision) } : {}),
            },
          }
        : {}),
      body: JSON.stringify(data),
    }),
  deleteCustomer: (id: string) =>
    request<{ success: boolean }>(`/api/customers/${id}`, { method: 'DELETE' }),
  bulkDeleteCustomers: (ids: string[]) =>
    request<{ deleted: number }>('/api/customers/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    }),

  // Projects
  listProjects: (opts: { archived?: boolean; customerId?: string } = {}) => {
    const params = new URLSearchParams()
    if (opts.archived) params.set('archived', 'true')
    if (opts.customerId) params.set('customer_id', opts.customerId)
    const qs = params.toString()
    return request<{ projects: Project[] }>(`/api/projects${qs ? `?${qs}` : ''}`)
  },
  getProject: (id: string) => request<{ project: Project }>(`/api/projects/${id}`),
  createProject: (
    data: {
      customerId: string
      name: string
      description?: string | null
      startDate?: string | null
      endDate?: string | null
      hourlyRateCents?: number | null
      budgetHours?: number | null
      color?: string | null
    } & EntitySaveOptions,
    idempotencyKey?: string,
    revision?: number,
  ) =>
    request<{ project: Project }>('/api/projects', {
      method: 'POST',
      ...(idempotencyKey
        ? {
            headers: {
              'Idempotency-Key': idempotencyKey,
              ...(revision !== undefined ? { 'Creation-Revision': String(revision) } : {}),
            },
          }
        : {}),
      body: JSON.stringify(data),
    }),
  updateProject: (
    id: string,
    data: Partial<{
      name: string
      customerId: string
      description: string | null
      startDate: string | null
      endDate: string | null
      hourlyRateCents: number | null
      budgetHours: number | null
      color: string | null
      archived: boolean
    }> &
      EntitySaveOptions,
    idempotencyKey?: string,
    revision?: number,
  ) =>
    request<{ project: Project }>(`/api/projects/${id}`, {
      method: 'PATCH',
      ...(idempotencyKey
        ? {
            headers: {
              'Idempotency-Key': idempotencyKey,
              ...(revision !== undefined ? { 'Creation-Revision': String(revision) } : {}),
            },
          }
        : {}),
      body: JSON.stringify(data),
    }),
  deleteProject: (id: string) =>
    request<{ success: boolean }>(`/api/projects/${id}`, { method: 'DELETE' }),
  bulkDeleteProjects: (ids: string[]) =>
    request<{ deleted: number }>('/api/projects/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    }),
  listProjectDocuments: (projectId: string) =>
    request<{ documents: DocumentSummary[] }>(`/api/projects/${projectId}/documents`),
  listProjectPages: (projectId: string) =>
    request<{ pages: ProjectPageSummary[] }>(`/api/projects/${projectId}/pages`),
  getProjectPage: (projectId: string, pageId: string) =>
    request<{ page: ProjectPage }>(`/api/projects/${projectId}/pages/${pageId}`),
  createProjectPage: (projectId: string, title: string) =>
    request<{ page: ProjectPage }>(`/api/projects/${projectId}/pages`, {
      method: 'POST',
      body: JSON.stringify({ title }),
    }),
  updateProjectPage: (
    projectId: string,
    pageId: string,
    patch: { title?: string; blocks?: ProjectPageBlock[]; baseUpdatedAt?: string },
  ) =>
    request<{ page: ProjectPage }>(`/api/projects/${projectId}/pages/${pageId}`, {
      method: 'PUT',
      body: JSON.stringify(patch),
    }),
  deleteProjectPage: (projectId: string, pageId: string) =>
    request<{ success: boolean }>(`/api/projects/${projectId}/pages/${pageId}`, {
      method: 'DELETE',
    }),
  listProjectActivity: (projectId: string, limit = 30) =>
    request<{ entries: ProjectActivityEntry[] }>(
      `/api/projects/${projectId}/activity?limit=${limit}`,
    ),
  uploadProjectDocuments: async (projectId: string, files: File[]) => {
    const fd = new FormData()
    for (const f of files) fd.append('file', f)
    const res = await apiFetch(`${BASE_URL}/api/projects/${projectId}/documents/upload`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    })
    if (!res.ok) {
      let m = `Upload failed (${res.status})`
      try {
        const j = (await res.json()) as { error?: string }
        if (j.error) m = j.error
      } catch {}
      throw new Error(m)
    }
    return (await res.json()) as { documents: DocumentSummary[] }
  },
  setProjectDocuments: (projectId: string, documentIds: string[]) =>
    request<{ success: boolean }>(`/api/projects/${projectId}/documents`, {
      method: 'PUT',
      body: JSON.stringify({ documentIds }),
    }),

  // Services
  listServices: (includeArchived = false) =>
    request<{ services: Service[] }>(`/api/services${includeArchived ? '?archived=true' : ''}`),
  createService: (data: {
    name: string
    defaultHourlyRateCents?: number | null
    billable?: boolean
  }) =>
    request<{ service: Service }>('/api/services', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateService: (
    id: string,
    data: Partial<{
      name: string
      defaultHourlyRateCents: number | null
      billable: boolean
      archived: boolean
    }>,
  ) =>
    request<{ service: Service }>(`/api/services/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  deleteService: (id: string) =>
    request<{ success: boolean }>(`/api/services/${id}`, { method: 'DELETE' }),
  bulkDeleteServices: (ids: string[]) =>
    request<{ deleted: number }>('/api/services/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    }),

  // Products (Stammdaten)
  listProducts: (includeArchived = false) =>
    request<{ products: Product[] }>(`/api/products${includeArchived ? '?archived=true' : ''}`),
  createProduct: (data: {
    name: string
    description?: string | null
    defaultPriceCents?: number
    unit?: string
    defaultTaxRatePercent?: number | null
  }) =>
    request<{ product: Product }>('/api/products', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateProduct: (
    id: string,
    data: Partial<{
      name: string
      description: string | null
      defaultPriceCents: number
      unit: string
      defaultTaxRatePercent: number | null
      archived: boolean
    }>,
  ) =>
    request<{ product: Product }>(`/api/products/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  deleteProduct: (id: string) =>
    request<{ success: boolean }>(`/api/products/${id}`, { method: 'DELETE' }),
  bulkDeleteProducts: (ids: string[]) =>
    request<{ deleted: number }>('/api/products/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    }),

  // Per-record access control (Stammdaten visibility)
  getEntityAccess: (resourceType: EntityResourceType, resourceId: string) =>
    request<EntityAccess>(`/api/access/${resourceType}/${resourceId}`),
  setEntityAccess: (
    resourceType: EntityResourceType,
    resourceId: string,
    payload: {
      restricted: boolean
      grants: Array<{
        subjectType: EntitySubjectType
        subjectId: string
        permission: EntityPermission
      }>
    },
  ) =>
    request<{ success: boolean; restricted: boolean; grantCount: number }>(
      `/api/access/${resourceType}/${resourceId}`,
      { method: 'PUT', body: JSON.stringify(payload) },
    ),
  bulkSetEntityAccess: (
    resourceType: EntityResourceType,
    payload: {
      resourceIds: string[]
      restricted: boolean
      grants: Array<{
        subjectType: EntitySubjectType
        subjectId: string
        permission: EntityPermission
      }>
    },
  ) =>
    request<{
      success: boolean
      updated: number
      requested: number
      restricted: boolean
      grantCount: number
    }>(`/api/access/${resourceType}/bulk`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  // Manual entity groups ("Ordner")
  listEntityGroups: (resourceType: EntityResourceType) =>
    request<{ groups: EntityGroupSummary[] }>(`/api/groups/${resourceType}`),
  createEntityGroup: (
    resourceType: EntityResourceType,
    payload: { name: string; color?: string | null; memberIds?: string[] },
  ) =>
    request<{ group: EntityGroupSummary }>(`/api/groups/${resourceType}`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  updateEntityGroup: (
    id: string,
    payload: Partial<{ name: string; color: string | null; sortOrder: number }>,
  ) =>
    request<{ group: EntityGroupSummary }>(`/api/groups/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  deleteEntityGroup: (id: string) =>
    request<{ success: boolean }>(`/api/groups/${id}`, { method: 'DELETE' }),
  addEntityGroupMembers: (id: string, memberIds: string[]) =>
    request<{ success: boolean; added: number }>(`/api/groups/${id}/members`, {
      method: 'POST',
      body: JSON.stringify({ memberIds }),
    }),
  removeEntityGroupMember: (id: string, resourceId: string) =>
    request<{ success: boolean }>(`/api/groups/${id}/members/${resourceId}`, {
      method: 'DELETE',
    }),

  // Workflows
  listWorkflows: () => request<{ workflows: Workflow[] }>('/api/workflows'),
  getWorkflow: (id: string) => request<{ workflow: Workflow }>(`/api/workflows/${id}`),
  listWorkflowRuns: (id: string) => request<{ runs: WorkflowRun[] }>(`/api/workflows/${id}/runs`),
  createWorkflow: (data: {
    name: string
    description?: string | null
    schedule: WorkflowScheduleKind
    scheduleConfig: WorkflowScheduleConfig
    actionType: WorkflowActionType
    actionConfig: Record<string, unknown>
    enabled?: boolean
  }) =>
    request<{ workflow: Workflow }>('/api/workflows', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateWorkflow: (
    id: string,
    data: Partial<{
      name: string
      description: string | null
      schedule: WorkflowScheduleKind
      scheduleConfig: WorkflowScheduleConfig
      actionType: WorkflowActionType
      actionConfig: Record<string, unknown>
      enabled: boolean
    }>,
  ) =>
    request<{ workflow: Workflow }>(`/api/workflows/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  deleteWorkflow: (id: string) =>
    request<{ success: boolean }>(`/api/workflows/${id}`, { method: 'DELETE' }),
  runWorkflow: (id: string) =>
    request<{ run: WorkflowRun }>(`/api/workflows/${id}/run`, { method: 'POST' }),

  // Chats
  listChats: () => request<{ chats: ChatListItem[] }>('/api/chats'),
  createChat: (data: { memberIds: string[]; name?: string; projectId?: string | null }) =>
    request<{ chat: { id: string; name: string | null; isGroup: boolean; createdAt: string } }>(
      '/api/chats',
      { method: 'POST', body: JSON.stringify(data) },
    ),
  listChatMessages: (
    chatId: string,
    opts: { before?: string; since?: string; limit?: number } = {},
  ) => {
    const p = new URLSearchParams()
    if (opts.before) p.set('before', opts.before)
    if (opts.since) p.set('since', opts.since)
    if (opts.limit) p.set('limit', String(opts.limit))
    const qs = p.toString()
    return request<{ messages: ChatMessage[] }>(
      `/api/chats/${chatId}/messages${qs ? `?${qs}` : ''}`,
    )
  },
  sendChatMessage: (chatId: string, content: string) =>
    request<{ message: ChatMessage }>(`/api/chats/${chatId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    }),
  markChatRead: (chatId: string) =>
    request<{ member: { lastReadAt: string } | null }>(`/api/chats/${chatId}/read`, {
      method: 'POST',
    }),
  uploadChatAttachment: async (chatId: string, files: File[], content: string) => {
    const fd = new FormData()
    if (content.trim()) fd.append('content', content)
    for (const file of files.slice(0, 4)) fd.append('file', file, file.name)
    const res = await apiFetch(`${BASE_URL}/api/chats/${chatId}/messages/upload`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    })
    if (!res.ok) {
      let m = `Upload fehlgeschlagen (${res.status})`
      try {
        const j = (await res.json()) as { error?: string }
        if (j.error) m = j.error
      } catch {}
      throw new Error(m)
    }
    return (await res.json()) as { message: ChatMessage }
  },
  sendVoiceMessage: async (chatId: string, blob: Blob, durationMs: number, filename: string) => {
    const fd = new FormData()
    fd.append('durationMs', String(Math.round(durationMs)))
    fd.append('file', blob, filename)
    const res = await apiFetch(`${BASE_URL}/api/chats/${chatId}/messages/voice`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    })
    if (!res.ok) {
      let m = `Senden fehlgeschlagen (${res.status})`
      try {
        const j = (await res.json()) as { error?: string }
        if (j.error) m = j.error
      } catch {}
      throw new Error(m)
    }
    return (await res.json()) as { message: ChatMessage }
  },
  transcribeAudio: async (blob: Blob, filename: string) => {
    const fd = new FormData()
    fd.append('file', blob, filename)
    const res = await apiFetch(`${BASE_URL}/api/ai/transcribe`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    })
    if (!res.ok) {
      let m = `Transkription fehlgeschlagen (${res.status})`
      try {
        const j = (await res.json()) as { error?: string }
        if (j.error) m = j.error
      } catch {}
      throw new Error(m)
    }
    return (await res.json()) as { text: string }
  },
  saveCopyFromChatMessage: (msgId: string, attachmentIdx: number) =>
    request<{ document: DocumentSummary }>(
      `/api/chats/messages/${msgId}/save-copy?attachmentIdx=${attachmentIdx}`,
      { method: 'POST' },
    ),
  shareDocumentAsChatLink: (
    chatId: string,
    body: { documentId: string; permission: 'view' | 'edit' },
  ) =>
    request<{ message: ChatMessage }>(`/api/chats/${chatId}/messages/share-link`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  requestAbsenceViaChat: (
    chatId: string,
    body: {
      type: AbsenceType
      fromDate: string
      toDate: string
      firstDayHalf?: AbsenceHalf | null
      lastDayHalf?: AbsenceHalf | null
      notes?: string | null
      substituteUserId?: string | null
    },
  ) =>
    request<{ message: ChatMessage; absence: Absence }>(
      `/api/chats/${chatId}/messages/absence-request`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  /** Inline-preview URL for a document's raw bytes. Honors document_shares
   * so chat link-recipients can render the image / open the file. */
  chatAttachmentRawUrl: (documentId: string) => `${BASE_URL}/api/documents/${documentId}/raw`,

  // Meetings
  listMeetings: (opts: { from?: string; to?: string; userId?: string } = {}) => {
    const p = new URLSearchParams()
    if (opts.from) p.set('from', opts.from)
    if (opts.to) p.set('to', opts.to)
    if (opts.userId) p.set('userId', opts.userId)
    const qs = p.toString()
    return request<{ meetings: Meeting[] }>(`/api/meetings${qs ? `?${qs}` : ''}`)
  },
  listRecentMeetings: (limit?: number) => {
    const qs = limit ? `?limit=${limit}` : ''
    return request<{ meetings: MeetingSummary[] }>(`/api/meetings/recent${qs}`)
  },
  getMeeting: (id: string) => request<{ meeting: Meeting }>(`/api/meetings/${id}`),
  createMeeting: (data: {
    title: string
    description?: string | null
    startTime: string
    endTime: string
    onlineUrl?: string | null
    location?: string | null
    countsAsWork?: boolean
    participantUserIds?: string[]
  }) =>
    request<{ meeting: Meeting }>('/api/meetings', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateMeeting: (
    id: string,
    data: Partial<{
      title: string
      description: string | null
      startTime: string
      endTime: string
      onlineUrl: string | null
      location: string | null
      countsAsWork: boolean
    }>,
  ) =>
    request<{ meeting: Meeting | null }>(`/api/meetings/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  deleteMeeting: (id: string) => request<void>(`/api/meetings/${id}`, { method: 'DELETE' }),
  addMeetingParticipant: (meetingId: string, userId: string) =>
    request<{ participant: { meetingId: string; userId: string; status: RsvpStatus } | null }>(
      `/api/meetings/${meetingId}/participants`,
      { method: 'POST', body: JSON.stringify({ userId }) },
    ),
  removeMeetingParticipant: (meetingId: string, userId: string) =>
    request<void>(`/api/meetings/${meetingId}/participants/${userId}`, { method: 'DELETE' }),
  setMeetingRsvp: (meetingId: string, status: RsvpStatus) =>
    request<{ participant: { meetingId: string; userId: string; status: RsvpStatus } | null }>(
      `/api/meetings/${meetingId}/rsvp`,
      { method: 'POST', body: JSON.stringify({ status }) },
    ),
  getMeetingJoinUrl: (meetingId: string) =>
    request<{ joinUrl: string | null; managed: boolean; moderator?: boolean }>(
      `/api/meetings/${meetingId}/join`,
    ),
  // #751 guest links (admin) + public guest endpoints
  listGuestLinks: (meetingId: string) =>
    request<{ links: MeetingGuestLink[] }>(`/api/meetings/${meetingId}/guest-links`),
  issueGuestLink: (meetingId: string) =>
    request<{ link: IssuedGuestLink }>(`/api/meetings/${meetingId}/guest-links`, {
      method: 'POST',
    }),
  revokeGuestLink: (meetingId: string, linkId: string) =>
    request<void>(`/api/meetings/${meetingId}/guest-links/${linkId}`, { method: 'DELETE' }),
  guestPreview: (token: string) =>
    guestPost('/api/guest/preview', { token }) as Promise<GuestPreview>,
  guestJoin: (token: string, name: string) =>
    guestPost('/api/guest/join', { token, name }) as Promise<GuestJoin>,

  // Calendar feed
  getCalendarFeedUrl: () => request<{ feedUrl: string; token: string }>('/api/calendar/feed-url'),
  regenerateCalendarFeedToken: () =>
    request<{ feedUrl: string; token: string }>('/api/calendar/feed-token/regenerate', {
      method: 'POST',
    }),

  // App settings (Meeting-Server-Config, etc.)
  getAppSettings: () => request<{ settings: AppSettings }>('/api/app-settings'),
  updateAppSettings: (
    data: Partial<{
      meetingBaseUrl: string | null
      meetingJwtAppId: string
      oauthMicrosoftClientId: string | null
      oauthMicrosoftClientSecret: string | null
      oauthMicrosoftTenant: string
      fastbillEmail: string | null
      fastbillApiKey: string | null
      fastbillProjectId: string | null
    }>,
  ) =>
    request<{ settings: AppSettings }>('/api/app-settings', {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  mailOAuthStartUrl: (params: { label?: string; fromName?: string }) => {
    const p = new URLSearchParams()
    if (params.label) p.set('label', params.label)
    if (params.fromName) p.set('fromName', params.fromName)
    return oauthLink(BASE_URL, `/api/mail/oauth/microsoft/start?${p.toString()}`)
  },
  mailOAuthAdminConsentUrl: () => oauthLink(BASE_URL, '/api/mail/oauth/microsoft/admin-consent'),
  rotateAppJwtSecret: () =>
    request<{ secret: string; rotatedAt: string }>('/api/app-settings/rotate-jwt-secret', {
      method: 'POST',
    }),

  // Web Push subscriptions
  getVapidPublicKey: () => request<{ publicKey: string }>('/api/push/vapid-public-key'),
  subscribePush: (data: { endpoint: string; keys: { p256dh: string; auth: string } }) =>
    request<{ subscription: { id: string; endpoint: string } }>('/api/push/subscribe', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  listPushSubscriptions: () =>
    request<{
      subscriptions: Array<{
        id: string
        endpoint: string
        userAgent: string | null
        createdAt: string
        lastUsedAt: string
      }>
    }>('/api/push/subscriptions'),
  unsubscribePush: (id: string) =>
    request<void>(`/api/push/subscriptions/${id}`, { method: 'DELETE' }),
  testPush: () => request<{ sent: number }>('/api/push/test', { method: 'POST' }),

  // Mail
  setMailAccountProtected: (id: string, protectedFlag: boolean) =>
    request<{ account: MailAccount }>(`/api/mail/accounts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ protected: protectedFlag }),
    }),
  setMailFolderProtected: (id: string, folderId: string, protectedFlag: boolean) =>
    request<{ folder: MailFolder }>(`/api/mail/accounts/${id}/folders/${folderId}`, {
      method: 'PATCH',
      body: JSON.stringify({ protected: protectedFlag }),
    }),
  listMailAccounts: () => request<{ accounts: MailAccount[] }>('/api/mail/accounts'),
  createMailAccount: (data: MailAccountInput) =>
    request<{ account: MailAccount }>('/api/mail/accounts', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  verifyMailAccount: (data: MailAccountInput) =>
    request<MailConnectionVerification>('/api/mail/accounts/verify', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  disableMailAccount: (id: string) =>
    request<{ account: MailAccount }>(`/api/mail/accounts/${id}/disable`, { method: 'POST' }),
  enableMailAccount: (id: string) =>
    request<{ account: MailAccount }>(`/api/mail/accounts/${id}/enable`, { method: 'POST' }),
  updateMailPush: (id: string, patch: { enabled?: boolean; content?: MailPushContent }) =>
    request<{ account: MailAccount }>(`/api/mail/accounts/${id}/push`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  deleteMailAccount: (id: string) =>
    request<void>(`/api/mail/accounts/${id}`, { method: 'DELETE' }),
  syncMailAccount: (id: string) =>
    request<{ folders: MailFolder[]; inbox: string; messages: MailMessage[] }>(
      `/api/mail/accounts/${id}/sync`,
      { method: 'POST' },
    ),
  listMailFolders: (id: string) =>
    request<{ folders: MailFolder[] }>(`/api/mail/accounts/${id}/folders`),
  listMailMessages: (
    id: string,
    opts: { folder?: string; limit?: number; refresh?: boolean } = {},
  ) => {
    const p = new URLSearchParams()
    if (opts.folder) p.set('folder', opts.folder)
    if (opts.limit) p.set('limit', String(opts.limit))
    if (opts.refresh) p.set('refresh', '1')
    const qs = p.toString()
    return request<{ messages: MailMessage[] }>(
      `/api/mail/accounts/${id}/messages${qs ? `?${qs}` : ''}`,
    )
  },
  listMailConversations: (
    id: string,
    opts: {
      folder?: string
      limit?: number
      refresh?: boolean
      filter?: MailListFilter
      /** Open mail whose thread stays listed under a chip (#778). */
      keep?: string
    } = {},
  ) => {
    const query = new URLSearchParams({
      folder: opts.folder ?? 'INBOX',
      limit: String(opts.limit ?? 50),
    })
    if (opts.refresh) query.set('refresh', '1')
    if (opts.filter && opts.filter !== 'all') {
      query.set('filter', opts.filter)
      if (opts.keep) query.set('keep', opts.keep)
    }
    return request<{ conversations: MailConversation[]; counts: Record<MailListFilter, number> }>(
      `/api/mail/accounts/${id}/conversations?${query}`,
    )
  },
  /** Unified inbox (#535): INBOX of every own account, chips as in a folder. */
  listMailUnifiedConversations: (
    opts: { limit?: number; filter?: MailListFilter; keep?: string } = {},
  ) => {
    const query = new URLSearchParams({ limit: String(opts.limit ?? 50) })
    if (opts.filter && opts.filter !== 'all') {
      query.set('filter', opts.filter)
      if (opts.keep) query.set('keep', opts.keep)
    }
    return request<{
      conversations: MailUnifiedConversation[]
      counts: Record<MailListFilter, number>
    }>(`/api/mail/unified/conversations?${query}`)
  },
  archiveMailMessage: (accountId: string, messageId: string) =>
    request<{ ok: true }>(`/api/mail/accounts/${accountId}/messages/${messageId}/archive`, {
      method: 'POST',
    }),
  /** Mail-KI-Helfer (#782): is the local AI switched on for mail? */
  getMailAssistStatus: () => request<{ enabled: boolean }>('/api/mail/assist/status'),
  /** One-off local AI read of a single mail; nothing is stored server-side. */
  mailAssist: (accountId: string, messageId: string, signal?: AbortSignal) =>
    request<{ result: MailAssistResult }>(
      `/api/mail/accounts/${accountId}/messages/${messageId}/assist`,
      { method: 'POST', signal },
    ),
  getMailMessage: (accountId: string, messageId: string) =>
    request<{ message: MailMessage; attachments: MailAttachment[] }>(
      `/api/mail/accounts/${accountId}/messages/${messageId}`,
    ),
  markMailRead: (accountId: string, messageId: string) =>
    request<{ ok: true }>(`/api/mail/accounts/${accountId}/messages/${messageId}/read`, {
      method: 'POST',
    }),
  markMailUnread: (accountId: string, messageId: string) =>
    request<{ ok: true }>(`/api/mail/accounts/${accountId}/messages/${messageId}/unread`, {
      method: 'POST',
    }),
  trashMailMessage: (accountId: string, messageId: string) =>
    request<{ ok: true }>(`/api/mail/accounts/${accountId}/messages/${messageId}/trash`, {
      method: 'POST',
    }),
  snoozeMailConversation: (accountId: string, messageIds: string[], until: string) =>
    request<{ snooze: { groupId: string; dueAt: string } }>(
      `/api/mail/accounts/${accountId}/snoozes`,
      { method: 'POST', body: JSON.stringify({ messageIds, until }) },
    ),
  cancelMailSnooze: (groupId: string) =>
    request<{ snooze: { groupId: string } }>(`/api/mail/snoozes/${groupId}`, { method: 'DELETE' }),
  listMailSnoozes: () => request<{ snoozes: MailSnoozeGroup[] }>('/api/mail/snoozes'),
  moveMailMessage: (accountId: string, messageId: string, targetPath: string) =>
    request<{ ok: true }>(`/api/mail/accounts/${accountId}/messages/${messageId}/move`, {
      method: 'POST',
      body: JSON.stringify({ targetPath }),
    }),
  flagMailMessage: (accountId: string, messageId: string, flagged: boolean) =>
    request<{ ok: true }>(`/api/mail/accounts/${accountId}/messages/${messageId}/flag`, {
      method: 'POST',
      body: JSON.stringify({ flagged }),
    }),
  mailAttachmentOptions: (s: MailAttachmentSource) =>
    request<MailAttachmentOptions>(
      `/api/mail/accounts/${s.accountId}/messages/${s.messageId}/attachments/${s.attachmentId}/actions`,
    ),
  mailAttachmentDrive: (s: MailAttachmentSource, folderId: string) =>
    request<MailAttachmentAction>(
      `/api/mail/accounts/${s.accountId}/messages/${s.messageId}/attachments/${s.attachmentId}/actions/drive`,
      { method: 'POST', body: JSON.stringify({ folderId }) },
    ),
  mailAttachmentReceipt: (s: MailAttachmentSource, mandantId: string) =>
    request<MailAttachmentAction>(
      `/api/mail/accounts/${s.accountId}/messages/${s.messageId}/attachments/${s.attachmentId}/actions/receipt`,
      { method: 'POST', body: JSON.stringify({ mandantId }) },
    ),
  documentMailSource: (id: string) =>
    request<MailAttachmentSource>(`/api/documents/${id}/mail-source`),
  mailAttachmentUrl: (accountId: string, messageId: string, attId: string) =>
    `${BASE_URL}/api/mail/accounts/${accountId}/messages/${messageId}/attachments/${attId}`,
  sendMail: (
    accountId: string,
    data: {
      to: string
      cc?: string
      bcc?: string
      subject: string
      text?: string
      html?: string
      forwardIntro?: string
      forwardSourceAccountId?: string
      forwardQuoteHtml?: string
      forwardQuoteText?: string
      forwardRemovedIds?: string[]
      inReplyTo?: string
      references?: string[]
      attachments?: SendMailAttachment[]
      /** Mail a reply or forward is based on (#535): the server refuses content
       * of a protected account or folder leaving through another account. */
      sourceMessageId?: string
      /** Saved draft this mail comes from (#830): its stored source counts too. */
      draftId?: string
      /** Sender identity of this account (#839); absent = default identity or
       * the account address. The outbox keeps it: never another sender. */
      identityId?: string
      autoAttachSignature?: boolean
    },
    /** One UUID per compose/outbox entry (#785): a repeated send with the same key
     * is answered `duplicate: true` without a second SMTP delivery. */
    idempotencyKey?: string,
  ) =>
    request<{ ok: true; duplicate?: boolean; messageId?: string }>(
      `/api/mail/accounts/${accountId}/send`,
      {
        method: 'POST',
        body: JSON.stringify(data),
        ...(idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {}),
      },
    ),
  /** Address suggestions from the history of this account only (#777/#820).
   * The signal lets react-query abort stale requests. */
  suggestMailAddresses: (accountId: string, q: string, limit: number, signal?: AbortSignal) =>
    request<{ suggestions: MailAddressSuggestion[] }>(
      `/api/mail/accounts/${accountId}/address-suggestions?q=${encodeURIComponent(q)}&limit=${limit}`,
      { signal },
    ).then((data) => data.suggestions),
  searchMail: (q: string, limit = 50, filter: MailListFilter = 'all') =>
    request<{ messages: MailMessage[] }>(
      `/api/mail/search?q=${encodeURIComponent(q)}&limit=${limit}${filter === 'all' ? '' : `&filter=${filter}`}`,
    ),
  listMailDrafts: (accountId?: string) =>
    request<{ drafts: MailDraft[] }>(
      `/api/mail/drafts${accountId ? `?accountId=${accountId}` : ''}`,
    ),
  getMailDraft: (id: string) => request<{ draft: MailDraft }>(`/api/mail/drafts/${id}`),
  saveMailDraft: (data: {
    id?: string | null
    accountId: string
    toAddrs?: string
    ccAddrs?: string
    bccAddrs?: string
    subject?: string
    bodyText?: string
    bodyHtml?: string
    forwardQuoteHtml?: string
    forwardQuoteText?: string
    forwardRemovedIds?: string[]
    inReplyTo?: string | null
    references?: string[]
    attachments?: SendMailAttachment[]
    /** Mail a reply or forward is based on (#830); the server keeps the first one. */
    sourceMessageId?: string
    autoAttachSignature?: boolean
    /** Sender identity chosen in the compose dialog (#839). */
    identityId?: string
  }) =>
    request<{ draft: MailDraft }>('/api/mail/drafts', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  deleteMailDraft: (id: string) => request<void>(`/api/mail/drafts/${id}`, { method: 'DELETE' }),
  // Sender identities of a mail account (#839). The password never comes back.
  listMailIdentities: (accountId: string) =>
    request<{ identities: PublicMailIdentity[] }>(`/api/mail/accounts/${accountId}/identities`),
  listMailCompanies: () => request<{ companies: PublicMailCompany[] }>('/api/mail/companies'),
  createMailCompany: (data: MailCompanyInput) =>
    request<{ company: PublicMailCompany }>('/api/mail/companies', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateMailCompany: (id: string, data: MailCompanyPatch) =>
    request<{ company: PublicMailCompany }>(`/api/mail/companies/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  deleteMailCompany: (id: string) =>
    request<void>(`/api/mail/companies/${id}`, { method: 'DELETE' }),
  listMailTemplates: (filter?: { companyId?: string; forIdentity?: string }) =>
    request<{ templates: PublicMailTemplate[] }>(
      `/api/mail/templates${filter ? `?${new URLSearchParams(Object.entries(filter).filter((entry): entry is [string, string] => !!entry[1])).toString()}` : ''}`,
    ),
  createMailTemplate: (data: MailTemplateInput) =>
    request<{ template: PublicMailTemplate }>('/api/mail/templates', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateMailTemplate: (id: string, data: MailTemplatePatch) =>
    request<{ template: PublicMailTemplate }>(`/api/mail/templates/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  deleteMailTemplate: (id: string) =>
    request<void>(`/api/mail/templates/${id}`, { method: 'DELETE' }),
  createMailIdentity: (accountId: string, data: MailIdentityCreateInput) =>
    request<{ identity: PublicMailIdentity }>(`/api/mail/accounts/${accountId}/identities`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateMailIdentity: (accountId: string, identityId: string, patch: MailIdentityPatchInput) =>
    request<{ identity: PublicMailIdentity }>(
      `/api/mail/accounts/${accountId}/identities/${identityId}`,
      { method: 'PATCH', body: JSON.stringify(patch) },
    ),
  deleteMailIdentity: (accountId: string, identityId: string) =>
    request<void>(`/api/mail/accounts/${accountId}/identities/${identityId}`, {
      method: 'DELETE',
    }),
  /** DNS check of a saved own-domain identity; stores the result on it. */
  checkMailIdentityDns: (accountId: string, identityId: string) =>
    request<{ identity: PublicMailIdentity; dns: MailIdentityDnsResult }>(
      `/api/mail/accounts/${accountId}/identities/${identityId}/dns-check`,
      { method: 'POST' },
    ),
  /** DNS check of an address that is not saved yet (form „Prüfen“). */
  checkMailIdentityDnsUnsaved: (accountId: string, address: string) =>
    request<{ dns: MailIdentityDnsResult }>(
      `/api/mail/accounts/${accountId}/identities/dns-check`,
      { method: 'POST', body: JSON.stringify({ address }) },
    ),
  /** SMTP connection test: unsaved form data, or `identityId` of a stored identity. */
  testMailIdentity: (accountId: string, data: MailIdentityTestInput) =>
    request<{ result: MailIdentityTestResult; identity?: PublicMailIdentity }>(
      `/api/mail/accounts/${accountId}/identities/test`,
      { method: 'POST', body: JSON.stringify(data) },
    ),
  /** `byAccount` (#535) feeds the account switcher; absent on older servers. */
  mailUnreadCount: () =>
    request<{ count: number; byAccount?: Record<string, number> }>('/api/mail/unread-count'),

  // Dashboard helpers
  fetchIconMeta: (url: string) =>
    request<{
      iconUrl: string | null
      bgColor: string | null
      width: number | null
      height: number | null
    }>(`/api/dashboard/icon-meta?url=${encodeURIComponent(url)}`),
  fetchIconColor: (url: string) =>
    request<{ bgColor: string | null }>(`/api/dashboard/icon-color?url=${encodeURIComponent(url)}`),
  fetchIconCandidates: (url: string) =>
    request<{
      candidates: Array<{
        iconUrl: string
        source: 'html' | 'convention' | 'external'
        declaredSize: number | null
        width: number | null
        height: number | null
        bgColor: string | null
        hint: string
      }>
    }>(`/api/dashboard/icon-candidates?url=${encodeURIComponent(url)}`),

  // Tags
  listTags: () => request<{ tags: Tag[] }>('/api/tags'),
  createTag: (data: { name: string; color?: string }) =>
    request<{ tag: Tag }>('/api/tags', { method: 'POST', body: JSON.stringify(data) }),
  updateTag: (id: string, data: Partial<{ name: string; color: string }>) =>
    request<{ tag: Tag }>(`/api/tags/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteTag: (id: string) => request<{ success: boolean }>(`/api/tags/${id}`, { method: 'DELETE' }),
  bulkDeleteTags: (ids: string[]) =>
    request<{ deleted: number }>('/api/tags/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    }),

  // Time entries
  listEntries: (
    opts: {
      from?: string
      to?: string
      projectId?: string
      search?: string
      tagIds?: string[]
      userId?: string
    } = {},
  ) => {
    const params = new URLSearchParams()
    if (opts.from) params.set('from', opts.from)
    if (opts.to) params.set('to', opts.to)
    if (opts.projectId) params.set('project_id', opts.projectId)
    if (opts.search) params.set('search', opts.search)
    if (opts.tagIds && opts.tagIds.length > 0) params.set('tag_ids', opts.tagIds.join(','))
    if (opts.userId) params.set('user_id', opts.userId)
    const qs = params.toString()
    return request<{ entries: TimeEntry[] }>(`/api/time-entries${qs ? `?${qs}` : ''}`)
  },
  startEntry: (data: {
    description: string
    projectId?: string | null
    serviceId?: string | null
    tagIds?: string[]
    notes?: string | null
  }) =>
    request<{ entry: TimeEntry }>('/api/time-entries/start', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  stopEntry: (id: string) =>
    request<{ entry: TimeEntry }>(`/api/time-entries/${id}/stop`, { method: 'POST' }),
  // Laufender Timer — unabhängig von Datums-Fenstern (ein über die
  // UTC-Mitternacht laufender Timer fällt aus einem from-Filter heraus).
  runningEntry: () => request<{ entry: TimeEntry | null }>('/api/time-entries/running'),
  // --- Fahrtenbuch ---
  listLogbookVehicles: () => request<{ vehicles: LogbookVehicle[] }>('/api/logbook/vehicles'),
  createLogbookVehicle: (data: { name: string; licensePlate?: string | null }) =>
    request<{ vehicle: LogbookVehicle }>('/api/logbook/vehicles', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateLogbookVehicle: (
    id: string,
    data: Partial<{ name: string; licensePlate: string | null; archived: boolean }>,
  ) =>
    request<{ vehicle: LogbookVehicle }>(`/api/logbook/vehicles/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  deleteLogbookVehicle: (id: string) =>
    request<{ success: boolean }>(`/api/logbook/vehicles/${id}`, { method: 'DELETE' }),
  listLogbookTrips: (vehicleId: string) =>
    request<{ trips: LogbookTrip[] }>(`/api/logbook/vehicles/${vehicleId}/trips`),
  createLogbookTrip: (vehicleId: string, data: LogbookTripInput) =>
    request<{ trip: LogbookTrip }>(`/api/logbook/vehicles/${vehicleId}/trips`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateLogbookTrip: (id: string, data: LogbookTripInput) =>
    request<{ trip: LogbookTrip }>(`/api/logbook/trips/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  deleteLogbookTrip: (id: string) =>
    request<{ success: boolean }>(`/api/logbook/trips/${id}`, { method: 'DELETE' }),
  reorderLogbookTrips: (vehicleId: string, orderedIds: string[]) =>
    request<{ trips: LogbookTrip[] }>(`/api/logbook/vehicles/${vehicleId}/trips/reorder`, {
      method: 'POST',
      body: JSON.stringify({ orderedIds }),
    }),
  /** Project + service of the most recent booking, for pre-filling the timer. */
  getLastSelection: () =>
    request<{ projectId: string | null; serviceId: string | null }>(
      '/api/time-entries/last-selection',
    ),
  /** Clockodo-artige Titel-Vorschläge aus dem eigenen Buchungs-Verlauf (ab 2 Zeichen). */
  getEntrySuggestions: (q: string, limit?: number) =>
    request<{ suggestions: EntrySuggestion[] }>(
      `/api/time-entries/suggestions?q=${encodeURIComponent(q)}${limit ? `&limit=${limit}` : ''}`,
    ),
  listTasks: (opts: { status?: string; projectId?: string } = {}) => {
    const p = new URLSearchParams()
    if (opts.status) p.set('status', opts.status)
    if (opts.projectId) p.set('project_id', opts.projectId)
    const qs = p.toString()
    return request<{ tasks: Task[] }>(`/api/tasks${qs ? `?${qs}` : ''}`)
  },
  createTask: (data: {
    title: string
    description?: string | null
    priority?: string
    dueDate?: string | null
    assigneeId?: string | null
    projectId?: string | null
  }) => request<{ task: Task }>('/api/tasks', { method: 'POST', body: JSON.stringify(data) }),
  updateTask: (
    id: string,
    data: Partial<{
      title: string
      description: string | null
      status: 'open' | 'in_progress' | 'done'
      priority: string
      dueDate: string | null
      assigneeId: string | null
      projectId: string | null
    }>,
  ) => request<{ task: Task }>(`/api/tasks/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteTask: (id: string) =>
    request<{ success: boolean }>(`/api/tasks/${id}`, { method: 'DELETE' }),
  createEntry: (data: {
    description: string
    startTime: string
    endTime?: string | null
    projectId?: string | null
    serviceId?: string | null
    billable?: boolean
    notes?: string | null
    tagIds?: string[]
    travelTimeToMinutes?: number
    travelTimeFromMinutes?: number
    travelCountsAsWork?: boolean
    meetingId?: string | null
    /** Admin/leader: create this entry for another user (defaults to self). */
    userId?: string
  }) =>
    request<{ entry: TimeEntry }>('/api/time-entries', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateEntry: (
    id: string,
    data: Partial<{
      description: string
      startTime: string
      endTime: string | null
      projectId: string | null
      serviceId: string | null
      billable: boolean
      notes: string | null
      tagIds: string[]
      travelTimeToMinutes: number
      travelTimeFromMinutes: number
      travelCountsAsWork: boolean
      meetingId: string | null
    }>,
  ) =>
    request<{ entry: TimeEntry }>(`/api/time-entries/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  deleteEntry: (id: string) =>
    request<{ success: boolean }>(`/api/time-entries/${id}`, { method: 'DELETE' }),
  splitEntry: (id: string, splitAt?: string) =>
    request<{ firstId: string; secondId: string }>(`/api/time-entries/${id}/split`, {
      method: 'POST',
      body: JSON.stringify(splitAt ? { splitAt } : {}),
    }),
  bulkDeleteEntries: (ids: string[]) =>
    request<{ deleted: number }>('/api/time-entries/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    }),

  // Change Requests (Änderungsanträge bei Edit-Lock)
  listChangeRequests: (opts: { status?: string; userId?: string } = {}) => {
    const p = new URLSearchParams()
    if (opts.status) p.set('status', opts.status)
    if (opts.userId) p.set('user_id', opts.userId)
    const qs = p.toString()
    return request<{
      requests: {
        id: string
        entryId: string
        entryType: string
        requestedBy: string
        desiredChanges: Record<string, unknown>
        reason: string | null
        status: 'pending' | 'approved' | 'declined' | 'cancelled'
        decidedBy: string | null
        decidedAt: string | null
        declineReason: string | null
        createdAt: string
      }[]
    }>(`/api/change-requests${qs ? `?${qs}` : ''}`)
  },
  createChangeRequest: (data: {
    entryId: string
    entryType?: 'time_entry' | 'work_time'
    desiredChanges: Record<string, unknown>
    reason?: string
  }) =>
    request<{ request: { id: string } }>('/api/change-requests', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  approveChangeRequest: (id: string) =>
    request<{ request: { id: string } }>(`/api/change-requests/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),
  declineChangeRequest: (id: string, reason?: string) =>
    request<{ request: { id: string } }>(`/api/change-requests/${id}/decline`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  cancelChangeRequest: (id: string) =>
    request<{ request: { id: string } }>(`/api/change-requests/${id}`, { method: 'DELETE' }),

  // Breaks
  listBreaks: (opts: { from?: string; to?: string; userId?: string } = {}) => {
    const params = new URLSearchParams()
    if (opts.from) params.set('from', opts.from)
    if (opts.to) params.set('to', opts.to)
    if (opts.userId) params.set('user_id', opts.userId)
    const qs = params.toString()
    return request<{ breaks: Break[] }>(`/api/breaks${qs ? `?${qs}` : ''}`)
  },
  runningBreak: () => request<{ break: Break | null }>('/api/breaks/running'),
  startBreak: (reason?: string) =>
    request<{ break: Break }>('/api/breaks/start', {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  stopBreak: (id: string) =>
    request<{ break: Break }>(`/api/breaks/${id}/stop`, { method: 'POST' }),
  deleteBreak: (id: string) =>
    request<{ success: boolean }>(`/api/breaks/${id}`, { method: 'DELETE' }),

  // Absences
  getAbsence: (id: string) => request<{ absence: Absence }>(`/api/absences/${id}`),
  listAbsences: (opts: { from?: string; to?: string; userId?: string } = {}) => {
    const params = new URLSearchParams()
    if (opts.from) params.set('from', opts.from)
    if (opts.to) params.set('to', opts.to)
    if (opts.userId) params.set('user_id', opts.userId)
    const qs = params.toString()
    return request<{ absences: Absence[] }>(`/api/absences${qs ? `?${qs}` : ''}`)
  },
  listTeamAbsences: (opts: { from?: string; to?: string } = {}) => {
    const params = new URLSearchParams()
    if (opts.from) params.set('from', opts.from)
    if (opts.to) params.set('to', opts.to)
    const qs = params.toString()
    return request<{ absences: Absence[] }>(`/api/absences/team${qs ? `?${qs}` : ''}`)
  },
  listTeamMembers: () =>
    request<{ users: { id: string; name: string; role: string }[] }>('/api/team-members'),

  // Teams (RBAC)
  listTeams: () =>
    request<{
      teams: {
        id: string
        name: string
        description: string | null
        archived: boolean
        members: { userId: string; role: string; name: string }[]
      }[]
    }>('/api/teams'),
  createTeam: (data: { name: string; description?: string | null }) =>
    request<{
      team: {
        id: string
        name: string
        description: string | null
        archived: boolean
        members: { userId: string; role: string; name: string }[]
      }
    }>('/api/teams', { method: 'POST', body: JSON.stringify(data) }),
  updateTeam: (
    id: string,
    data: Partial<{ name: string; description: string | null; archived: boolean }>,
  ) =>
    request<{ team: { id: string; name: string; description: string | null; archived: boolean } }>(
      `/api/teams/${id}`,
      { method: 'PATCH', body: JSON.stringify(data) },
    ),
  deleteTeam: (id: string) =>
    request<{ success: boolean }>(`/api/teams/${id}`, { method: 'DELETE' }),
  addTeamMember: (teamId: string, userId: string, role: 'leader' | 'member' = 'member') =>
    request<{ member: { teamId: string; userId: string; role: string } }>(
      `/api/teams/${teamId}/members`,
      { method: 'POST', body: JSON.stringify({ userId, role }) },
    ),
  updateTeamMemberRole: (teamId: string, userId: string, role: 'leader' | 'member') =>
    request<{ member: { teamId: string; userId: string; role: string } }>(
      `/api/teams/${teamId}/members/${userId}`,
      { method: 'PATCH', body: JSON.stringify({ role }) },
    ),
  removeTeamMember: (teamId: string, userId: string) =>
    request<{ success: boolean }>(`/api/teams/${teamId}/members/${userId}`, { method: 'DELETE' }),
  createAbsence: (data: {
    type: AbsenceType
    fromDate: string
    toDate: string
    notes?: string | null
    firstDayHalf?: AbsenceHalf | null
    lastDayHalf?: AbsenceHalf | null
    substituteUserId?: string | null
    userId?: string
  }) =>
    request<{ absence: Absence; warning?: string | null }>('/api/absences', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateAbsence: (
    id: string,
    data: Partial<{
      type: AbsenceType
      fromDate: string
      toDate: string
      notes: string | null
      firstDayHalf: AbsenceHalf | null
      lastDayHalf: AbsenceHalf | null
      substituteUserId: string | null
    }>,
  ) =>
    request<{ absence: Absence; warning?: string | null }>(`/api/absences/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  uploadAbsenceSickNote: async (id: string, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    const res = await apiFetch(`${BASE_URL}/api/absences/${id}/sick-note`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    })
    if (!res.ok) {
      let m = `Upload fehlgeschlagen (${res.status})`
      try {
        const j = (await res.json()) as { error?: string }
        if (j.error) m = j.error
      } catch {}
      throw new Error(m)
    }
    return (await res.json()) as {
      absence: Absence
      document: { id: string; filename: string }
    }
  },
  deleteAbsence: (id: string) =>
    request<{ success: boolean }>(`/api/absences/${id}`, { method: 'DELETE' }),
  reviewAbsence: (id: string, status: 'approved' | 'rejected', note?: string) =>
    request<{ absence: Absence }>(`/api/absences/${id}/review`, {
      method: 'PATCH',
      body: JSON.stringify({ status, note }),
    }),
  listPendingAbsences: () => request<{ absences: Absence[] }>('/api/absences/pending'),
  listAllAbsences: () => request<{ absences: Absence[] }>('/api/absences/all'),
  listRecentReviewAbsences: () => request<{ absences: Absence[] }>('/api/absences/recent-reviews'),
  withdrawAbsence: (id: string) =>
    request<{ absence: Absence }>(`/api/absences/${id}/withdraw`, { method: 'POST' }),

  // Vacation blackouts (Sperrzeiten)
  listVacationBlackouts: () =>
    request<{ blackouts: VacationBlackout[] }>('/api/vacation-blackouts'),
  createVacationBlackout: (data: {
    fromDate: string
    toDate: string
    name: string
    reason?: string | null
    blockMode?: BlackoutMode
    teamId?: string | null
  }) =>
    request<{ blackout: VacationBlackout }>('/api/vacation-blackouts', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateVacationBlackout: (
    id: string,
    data: Partial<{
      fromDate: string
      toDate: string
      name: string
      reason: string | null
      blockMode: BlackoutMode
      teamId: string | null
    }>,
  ) =>
    request<{ blackout: VacationBlackout }>(`/api/vacation-blackouts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  deleteVacationBlackout: (id: string) =>
    request<{ success: boolean }>(`/api/vacation-blackouts/${id}`, { method: 'DELETE' }),

  // Vacation rules (Vorlaufzeit + Max-Dauer)
  getVacationRules: () => request<{ rules: VacationRules }>('/api/app-settings/vacation-rules'),
  updateVacationRules: (data: Partial<VacationRules>) =>
    request<{ rules: VacationRules }>('/api/app-settings/vacation-rules', {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  // Absence cancellation workflow (Stornierung)
  requestAbsenceCancellation: (id: string, reason?: string) =>
    request<{ absence: Absence }>(`/api/absences/${id}/request-cancellation`, {
      method: 'POST',
      body: JSON.stringify({ reason: reason ?? '' }),
    }),
  approveAbsenceCancellation: (id: string) =>
    request<{ success: boolean }>(`/api/absences/${id}/approve-cancellation`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),
  rejectAbsenceCancellation: (id: string, note?: string) =>
    request<{ absence: Absence }>(`/api/absences/${id}/reject-cancellation`, {
      method: 'POST',
      body: JSON.stringify({ note: note ?? '' }),
    }),

  // Team calendar feed (supervisor/admin) — reuses the user's personal
  // calendarFeedToken (no separate token to manage). Backend resolves
  // scope based on viewer role: admin sees all, supervisor sees subordinates.
  getTeamCalendarFeedUrl: async () => {
    const { feedUrl, token } = await request<{ feedUrl: string; token: string }>(
      '/api/calendar/feed-url',
    )
    const base = feedUrl.replace('/calendar/feed.ics', '/calendar/team-absences.ics').split('?')[0]
    return { feedUrl: `${base}?token=${token}`, token }
  },

  // Work-time (Stempeluhr)
  workTimeStatus: () =>
    request<{
      state: 'clocked_out' | 'clocked_in' | 'on_break'
      sinceAt: string | null
    }>('/api/work-time/status'),
  workTimeClockIn: () =>
    request<{ event: { id: string; type: string; occurredAt: string }; state: string }>(
      '/api/work-time/clock-in',
      { method: 'POST', body: JSON.stringify({}) },
    ),
  workTimeClockOut: () =>
    request<{ event: { id: string }; state: string }>('/api/work-time/clock-out', {
      method: 'POST',
      body: JSON.stringify({}),
    }),
  workTimeBreakIn: () =>
    request<{ event: { id: string }; state: string }>('/api/work-time/break-in', {
      method: 'POST',
      body: JSON.stringify({}),
    }),
  workTimeBreakOut: () =>
    request<{ event: { id: string }; state: string }>('/api/work-time/break-out', {
      method: 'POST',
      body: JSON.stringify({}),
    }),
  workTimeSummary: (date?: string) =>
    request<{
      workMinutes: number
      breakMinutes: number
      blocks: { startAt: string; endAt: string | null; type: 'work' | 'break' }[]
    }>(`/api/work-time/summary${date ? `?date=${date}` : ''}`),

  // Surcharges (Zuschläge)
  // #1861 Coaching-Abo (Dennis' view)
  coachingAbo: (areaId?: string) =>
    request<CoachingAboUebersicht>(
      `/api/coaching/abo${areaId ? `?areaId=${encodeURIComponent(areaId)}` : ''}`,
    ),
  coachingAboEinstellungen: (body: unknown) =>
    request<{ ok: boolean }>('/api/coaching/abo/einstellungen', {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  coachingAboVerbrauch: (
    id: string,
    body: {
      requestId: string
      art: 'chat_kurz' | 'chat_lang' | 'korrektur'
      minuten?: number
      notiz?: string
    },
  ) =>
    request<{ ok: boolean; minuten: number }>(`/api/coaching/abo/${id}/verbrauch`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  coachingGruppentermin: (body: unknown) =>
    request<{ id: string }>('/api/coaching/abo/gruppentermine', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  listSurcharges: () =>
    request<{
      surcharges: {
        id: string
        name: string
        type: string
        appliesOnWeekdays: number[] | null
        appliesOnHoliday: boolean
        activeFromTime: string | null
        activeUntilTime: string | null
        thresholdHoursDaily: string | null
        thresholdHoursWeekly: string | null
        percentage: string
        stackable: boolean
        exclusiveGroup: string | null
        active: boolean
      }[]
    }>('/api/surcharges'),
  createSurcharge: (data: {
    name: string
    type?: string
    appliesOnWeekdays?: number[]
    appliesOnHoliday?: boolean
    activeFromTime?: string
    activeUntilTime?: string
    thresholdHoursDaily?: number
    thresholdHoursWeekly?: number
    percentage: number
    stackable?: boolean
    exclusiveGroup?: string
  }) =>
    request<{ surcharge: { id: string } }>('/api/surcharges', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateSurcharge: (id: string, data: Record<string, unknown>) =>
    request<{ surcharge: { id: string } }>(`/api/surcharges/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  deleteSurcharge: (id: string) =>
    request<{ success: boolean }>(`/api/surcharges/${id}`, { method: 'DELETE' }),

  // Holidays
  listNonbusinessgroups: () => request<{ groups: Nonbusinessgroup[] }>('/api/nonbusinessgroups'),
  listNonbusinessdays: (
    opts: { groupId?: string; year?: number; from?: string; to?: string } = {},
  ) => {
    const p = new URLSearchParams()
    if (opts.groupId) p.set('group_id', opts.groupId)
    if (opts.year) p.set('year', String(opts.year))
    if (opts.from) p.set('from', opts.from)
    if (opts.to) p.set('to', opts.to)
    const qs = p.toString()
    return request<{ days: Nonbusinessday[] }>(`/api/nonbusinessdays${qs ? `?${qs}` : ''}`)
  },

  // Break rules (statutory pause minimums)
  listBreakRules: () => request<{ rules: BreakRule[] }>('/api/break-rules'),
  createBreakRule: (data: {
    name: string
    country: string
    region?: string | null
    tiers: BreakRuleTier[]
  }) =>
    request<{ rule: BreakRule }>('/api/break-rules', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateBreakRule: (
    id: string,
    data: Partial<{ name: string; country: string; region: string | null; tiers: BreakRuleTier[] }>,
  ) =>
    request<{ rule: BreakRule }>(`/api/break-rules/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  deleteBreakRule: (id: string) =>
    request<{ success: boolean }>(`/api/break-rules/${id}`, { method: 'DELETE' }),
  createNonbusinessday: (data: {
    groupId: string
    date: string
    name: string
    halfDay?: boolean
  }) =>
    request<{ day: Nonbusinessday }>('/api/nonbusinessdays', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateNonbusinessday: (id: string, data: { name?: string; halfDay?: boolean }) =>
    request<{ day: Nonbusinessday }>(`/api/nonbusinessdays/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  deleteNonbusinessday: (id: string) =>
    request<{ success: boolean }>(`/api/nonbusinessdays/${id}`, { method: 'DELETE' }),
  createNonbusinessgroup: (data: { name: string; country?: string; region?: string }) =>
    request<{ group: Nonbusinessgroup }>('/api/nonbusinessgroups', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  deleteNonbusinessgroup: (id: string) =>
    request<{ success: boolean }>(`/api/nonbusinessgroups/${id}`, { method: 'DELETE' }),

  // Reports
  dailyReport: (date?: string) =>
    request<DailyReport>(`/api/reports/daily${date ? `?date=${date}` : ''}`),
  weeklyReport: (week?: string) =>
    request<WeeklyReport>(`/api/reports/weekly${week ? `?week=${week}` : ''}`),
  rangeReport: (opts: { from?: string; to?: string; projectId?: string } = {}) => {
    const p = new URLSearchParams()
    if (opts.from) p.set('from', opts.from)
    if (opts.to) p.set('to', opts.to)
    if (opts.projectId) p.set('project_id', opts.projectId)
    const qs = p.toString()
    return request<RangeReport>(`/api/reports/range${qs ? `?${qs}` : ''}`)
  },
  csvExportUrl: (opts: { from?: string; to?: string; projectId?: string } = {}) => {
    const p = new URLSearchParams()
    if (opts.from) p.set('from', opts.from)
    if (opts.to) p.set('to', opts.to)
    if (opts.projectId) p.set('project_id', opts.projectId)
    const qs = p.toString()
    return `${BASE_URL}/api/reports/csv${qs ? `?${qs}` : ''}`
  },
  performanceReport: (opts: { scope?: string; from?: string; to?: string } = {}) => {
    const p = new URLSearchParams()
    if (opts.scope) p.set('scope', opts.scope)
    if (opts.from) p.set('from', opts.from)
    if (opts.to) p.set('to', opts.to)
    const qs = p.toString()
    return request<PerformanceReport>(`/api/reports/performance${qs ? `?${qs}` : ''}`)
  },
  teamStatus: () => request<TeamStatusReport>('/api/reports/team-status'),
  complianceReport: (opts: { from: string; to: string; userId?: string }) => {
    const p = new URLSearchParams()
    p.set('from', opts.from)
    p.set('to', opts.to)
    if (opts.userId) p.set('userId', opts.userId)
    return request<ComplianceReport>(`/api/reports/compliance?${p.toString()}`)
  },
  teamYearSummary: (opts: { year?: number } = {}) => {
    const p = new URLSearchParams()
    if (opts.year != null) p.set('year', String(opts.year))
    const qs = p.toString()
    return request<TeamYearSummary>(`/api/reports/team-year-summary${qs ? `?${qs}` : ''}`)
  },

  // ── Import ────────────────────────────────────────────────────────
  importDetect: async (file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    const res = await apiFetch(`${BASE_URL}/api/import/detect`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    })
    if (!res.ok) throw new Error(await res.text())
    return (await res.json()) as {
      tables: {
        filename: string
        guessTable: string | null
        columns: string[]
        rowCount: number
        sample: Record<string, string>[]
      }[]
      knownTargets: string[]
    }
  },
  listImportPresets: () => request<{ presets: ImportPreset[] }>('/api/import/presets'),
  saveImportPreset: (body: {
    sourceFilename: string
    targetTable: string
    label?: string | null
    mapping: Record<string, unknown>
  }) =>
    request<{ preset: ImportPreset }>('/api/import/presets', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  importRun: async (
    file: File,
    mapping: Record<string, { targetTable: string; mapping: Record<string, unknown> }>,
    options: { defaultPassword?: string; dryRun?: boolean } = {},
  ) => {
    const fd = new FormData()
    fd.append('file', file)
    fd.append('mapping', JSON.stringify(mapping))
    fd.append('options', JSON.stringify(options))
    const res = await apiFetch(`${BASE_URL}/api/import/run`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    })
    if (!res.ok) throw new Error(await res.text())
    return (await res.json()) as {
      summary: {
        filename: string
        targetTable: string
        inserted: number
        updated: number
        skipped: number
        errors: string[]
      }[]
      dryRun: boolean
    }
  },

  pdfReportUrl: (opts: { from?: string; to?: string; projectId?: string } = {}) => {
    const p = new URLSearchParams()
    if (opts.from) p.set('from', opts.from)
    if (opts.to) p.set('to', opts.to)
    if (opts.projectId) p.set('project_id', opts.projectId)
    const qs = p.toString()
    return `${BASE_URL}/api/reports/pdf${qs ? `?${qs}` : ''}`
  },

  // Time Accounts (Stundenkonten)
  getTimeAccounts: (opts: { userId?: string; year?: number } = {}) => {
    const p = new URLSearchParams()
    if (opts.userId) p.set('userId', opts.userId)
    if (opts.year) p.set('year', String(opts.year))
    const qs = p.toString()
    return request<TimeAccountData>(`/api/time-accounts${qs ? `?${qs}` : ''}`)
  },

  // Work Schedules
  listWorkSchedules: (userId?: string) => {
    const p = new URLSearchParams()
    if (userId) p.set('userId', userId)
    const qs = p.toString()
    return request<{ schedules: WorkSchedule[] }>(`/api/work-schedules${qs ? `?${qs}` : ''}`)
  },
  createWorkSchedule: (data: {
    userId: string
    label?: string | null
    validFrom: string
    validTo?: string | null
    weeklyHours: number
    monHours: number
    tueHours: number
    wedHours: number
    thuHours: number
    friHours: number
    satHours: number
    sunHours: number
  }) =>
    request<{ schedule: WorkSchedule }>('/api/work-schedules', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateWorkSchedule: (
    id: string,
    data: Partial<{
      label: string | null
      validFrom: string
      validTo: string | null
      weeklyHours: number
      monHours: number
      tueHours: number
      wedHours: number
      thuHours: number
      friHours: number
      satHours: number
      sunHours: number
    }>,
  ) =>
    request<{ schedule: WorkSchedule }>(`/api/work-schedules/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  deleteWorkSchedule: (id: string) =>
    request<{ success: boolean }>(`/api/work-schedules/${id}`, { method: 'DELETE' }),

  // Hour Adjustments
  listHourAdjustments: (opts: { userId?: string; year?: number } = {}) => {
    const p = new URLSearchParams()
    if (opts.userId) p.set('userId', opts.userId)
    if (opts.year) p.set('year', String(opts.year))
    const qs = p.toString()
    return request<{ adjustments: HourAdjustment[] }>(`/api/hour-adjustments${qs ? `?${qs}` : ''}`)
  },
  createHourAdjustment: (data: {
    userId: string
    type: 'carryover' | 'payout' | 'correction'
    date: string
    minutes: number
    description?: string | null
  }) =>
    request<{ adjustment: HourAdjustment }>('/api/hour-adjustments', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateHourAdjustment: (
    id: string,
    data: Partial<{
      type: 'carryover' | 'payout' | 'correction'
      date: string
      minutes: number
      description: string | null
    }>,
  ) =>
    request<{ adjustment: HourAdjustment }>(`/api/hour-adjustments/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  deleteHourAdjustment: (id: string) =>
    request<{ success: boolean }>(`/api/hour-adjustments/${id}`, { method: 'DELETE' }),

  // Vacation Adjustments
  listVacationAdjustments: (opts: { userId?: string; year?: number } = {}) => {
    const p = new URLSearchParams()
    if (opts.userId) p.set('userId', opts.userId)
    if (opts.year) p.set('year', String(opts.year))
    const qs = p.toString()
    return request<{ adjustments: VacationAdjustment[] }>(
      `/api/vacation-adjustments${qs ? `?${qs}` : ''}`,
    )
  },
  createVacationAdjustment: (data: {
    userId: string
    type: VacationAdjustmentType
    year: number
    days: number
    description?: string | null
    expiresAt?: string | null
  }) =>
    request<{ adjustment: VacationAdjustment }>('/api/vacation-adjustments', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateVacationAdjustment: (
    id: string,
    data: Partial<{
      type: VacationAdjustmentType
      year: number
      days: number
      description: string | null
      expiresAt: string | null
    }>,
  ) =>
    request<{ adjustment: VacationAdjustment }>(`/api/vacation-adjustments/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  deleteVacationAdjustment: (id: string) =>
    request<{ success: boolean }>(`/api/vacation-adjustments/${id}`, { method: 'DELETE' }),

  // Vacation Entitlements
  listVacationEntitlements: (userId?: string) => {
    const p = new URLSearchParams()
    if (userId) p.set('userId', userId)
    const qs = p.toString()
    return request<{ entitlements: VacationEntitlement[] }>(
      `/api/vacation-entitlements${qs ? `?${qs}` : ''}`,
    )
  },
  createVacationEntitlement: (data: {
    userId: string
    validFrom: string
    validTo?: string | null
    daysPerYear: number
    notes?: string | null
  }) =>
    request<{ entitlement: VacationEntitlement }>('/api/vacation-entitlements', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateVacationEntitlement: (
    id: string,
    data: Partial<{
      validFrom: string
      validTo: string | null
      daysPerYear: number
      notes: string | null
    }>,
  ) =>
    request<{ entitlement: VacationEntitlement }>(`/api/vacation-entitlements/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  deleteVacationEntitlement: (id: string) =>
    request<{ success: boolean }>(`/api/vacation-entitlements/${id}`, { method: 'DELETE' }),

  // Documents
  listDocuments: (opts: { incoming?: boolean; userId?: string } = {}) => {
    const p = new URLSearchParams()
    if (opts.incoming) p.set('incoming', 'true')
    if (opts.userId) p.set('userId', opts.userId)
    const qs = p.toString()
    return request<{ documents: DocumentSummary[]; signatures: DocSignature[] }>(
      `/api/documents${qs ? `?${qs}` : ''}`,
    )
  },
  documentDetail: (id: string) =>
    request<{
      document: DocumentSummary
      signatures: DocSignature[]
      auditLog: DocAuditEntry[]
    }>(`/api/documents/${id}`),
  documentSendableUsers: () =>
    request<{ users: { id: string; name: string; email: string; role: string }[] }>(
      '/api/documents/sendable-users',
    ),
  uploadDocument: async (file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    const res = await apiFetch(`${BASE_URL}/api/documents`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    })
    if (!res.ok) {
      let m = `Upload failed (${res.status})`
      try {
        const j = (await res.json()) as { error?: string }
        if (j.error) m = j.error
      } catch {}
      throw new Error(m)
    }
    return (await res.json()) as { document: DocumentSummary }
  },
  sendDocument: (id: string, body: { recipientUserIds: string[]; message?: string | null }) =>
    request<{ signatures: DocSignature[] }>(`/api/documents/${id}/send`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  signDocument: (
    sigId: string,
    body: { date: string; location: string; signatureImageBase64: string },
  ) =>
    request<{ signature: DocSignature }>(`/api/documents/signatures/${sigId}/sign`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  rejectDocument: (sigId: string, reason: string | null) =>
    request<{ signature: DocSignature }>(`/api/documents/signatures/${sigId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  archiveDocument: (id: string) =>
    request<{ ok: boolean }>(`/api/documents/${id}`, { method: 'DELETE' }),
  bulkArchiveDocuments: (ids: string[]) =>
    request<{ archived: number; failed: number }>('/api/documents/bulk-archive', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    }),
  updateDocument: (id: string, patch: { filename?: string; projectId?: string | null }) =>
    request<{ document: DocumentSummary }>(`/api/documents/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  documentFileUrl: (id: string) => `${BASE_URL}/api/documents/${id}/file`,
  documentSignedFileUrl: (id: string, sigId: string) =>
    `${BASE_URL}/api/documents/${id}/signed/${sigId}`,

  // Invoices
  listInvoices: (opts: { status?: string; customerId?: string } = {}) => {
    const p = new URLSearchParams()
    if (opts.status) p.set('status', opts.status)
    if (opts.customerId) p.set('customer_id', opts.customerId)
    const qs = p.toString()
    return request<{ invoices: InvoiceListItem[] }>(`/api/invoices${qs ? `?${qs}` : ''}`)
  },
  getInvoice: (id: string) => request<{ invoice: InvoiceDetail }>(`/api/invoices/${id}`),
  invoiceBuilderEntries: (
    opts: {
      customerId?: string
      projectIds?: string[]
      userIds?: string[]
      from?: string
      to?: string
      excludeBilled?: boolean
    } = {},
  ) => {
    const p = new URLSearchParams()
    if (opts.customerId) p.set('customer_id', opts.customerId)
    if (opts.projectIds?.length) p.set('project_ids', opts.projectIds.join(','))
    if (opts.userIds?.length) p.set('user_ids', opts.userIds.join(','))
    if (opts.from) p.set('from', opts.from)
    if (opts.to) p.set('to', opts.to)
    if (opts.excludeBilled) p.set('exclude_billed', 'true')
    const qs = p.toString()
    return request<{ entries: BuilderEntry[] }>(
      `/api/invoices/builder/entries${qs ? `?${qs}` : ''}`,
    )
  },
  invoicePreview: (body: {
    entryIds: string[]
    taxRatePercent?: number
    groupBy?: 'none' | 'project' | 'user'
  }) =>
    request<{ preview: InvoicePreview }>('/api/invoices/preview', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  nextInvoiceNumber: () => request<{ invoiceNumber: string }>('/api/invoices/next-number'),
  createInvoice: (body: CreateInvoicePayload) =>
    request<{ invoice: InvoiceDetail }>('/api/invoices', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /** Pushes the previewed invoice to Fastbill as a draft (no local invoice
   * record is created). Returns the new Fastbill invoice id + a deep link. */
  createFastbillDraft: (body: {
    customerId: string
    notes?: string | null
    taxRatePercent?: number
    lineItems: Array<{
      description: string
      quantity: number
      unitPriceCents: number
      taxRatePercent?: number
    }>
  }) =>
    request<{ fastbillInvoiceId: string; fastbillUrl: string | null }>(
      '/api/invoices/fastbill-draft',
      { method: 'POST', body: JSON.stringify(body) },
    ),
  /** Pulls customers from Fastbill and upserts them locally (admin-only).
   * Returns a {pulled,created,linked,skipped} summary. */
  syncFastbillCustomers: () =>
    request<{ pulled: number; created: number; linked: number; skipped: number }>(
      '/api/fastbill/sync-customers',
      { method: 'POST' },
    ),
  /** Pulls projects from Fastbill and upserts them under their mapped customer
   * (admin-only). Returns a {pulled,created,linked,skipped} summary. */
  syncFastbillProjects: () =>
    request<{ pulled: number; created: number; linked: number; skipped: number }>(
      '/api/fastbill/sync-projects',
      { method: 'POST' },
    ),
  /** Pushes a STORED invoice to Fastbill as a draft and back-links the id.
   * Idempotent — a re-push returns the existing draft (alreadyPushed). */
  pushInvoiceFastbill: (id: string) =>
    request<{ fastbillInvoiceId: string; fastbillUrl: string | null; alreadyPushed: boolean }>(
      `/api/invoices/${id}/push-fastbill`,
      { method: 'POST' },
    ),
  // === emberfibu-Abrechnung (#1007) ===
  fibuStatus: () => request<FibuStatus>('/api/fibu/status'),
  fibuZeiten: (opts: { customerId: string; projectIds?: string[]; from?: string; to?: string }) => {
    const p = new URLSearchParams({ customer_id: opts.customerId })
    if (opts.projectIds?.length) p.set('project_ids', opts.projectIds.join(','))
    if (opts.from) p.set('from', opts.from)
    if (opts.to) p.set('to', opts.to)
    return request<{ entries: BuilderEntry[]; offen: FibuUebergabe[] }>(`/api/fibu/zeiten?${p}`)
  },
  fibuZiel: (customerId: string, projectIds: string[] = []) => {
    const p = new URLSearchParams({ customer_id: customerId })
    if (projectIds.length) p.set('project_ids', projectIds.join(','))
    return request<FibuZielVorschlag>(`/api/fibu/ziel?${p}`)
  },
  fibuVorschau: (body: FibuAuswahl) =>
    request<{ vorschau: FibuVorschau }>('/api/fibu/vorschau', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  fibuUebergeben: (body: FibuAuswahl) =>
    request<{ uebergabe: FibuUebergabe; wiederholt: boolean }>('/api/fibu/uebergaben', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  fibuUebergaben: () => request<{ uebergaben: FibuUebergabe[] }>('/api/fibu/uebergaben'),
  fibuKundenFirma: (customerId: string) =>
    request<{ firmaId: string | null }>(`/api/fibu/kunden/${customerId}/firma`),
  setFibuKundenFirma: (customerId: string, firmaId: string | null) =>
    request<{ firmaId: string | null }>(`/api/fibu/kunden/${customerId}/firma`, {
      method: 'PUT',
      body: JSON.stringify({ firmaId }),
    }),
  fibuAbgleich: (id: string) =>
    request<{ uebergabe: FibuUebergabe }>(`/api/fibu/uebergaben/${id}/abgleich`, {
      method: 'POST',
    }),
  fibuWiederholen: (id: string) =>
    request<{ uebergabe: FibuUebergabe }>(`/api/fibu/uebergaben/${id}/wiederholen`, {
      method: 'POST',
    }),
  fibuVerwerfen: (id: string) =>
    request<{ uebergabe: FibuUebergabe }>(`/api/fibu/uebergaben/${id}/verwerfen`, {
      method: 'POST',
    }),
  fibuProjektFirma: (projectId: string) =>
    request<{ firmaId: string | null }>(`/api/fibu/projekte/${projectId}/firma`),
  setFibuProjektFirma: (projectId: string, firmaId: string | null) =>
    request<{ firmaId: string | null }>(`/api/fibu/projekte/${projectId}/firma`, {
      method: 'PUT',
      body: JSON.stringify({ firmaId }),
    }),
  updateInvoice: (id: string, body: UpdateInvoicePayload) =>
    request<{ invoice: InvoiceListItem }>(`/api/invoices/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteInvoice: (id: string) =>
    request<{ success: boolean }>(`/api/invoices/${id}`, { method: 'DELETE' }),
  addInvoiceLineItem: (invoiceId: string, body: AddLineItemPayload) =>
    request<{ item: InvoiceLineItem; invoice: InvoiceDetail }>(`/api/invoices/${invoiceId}/items`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateInvoiceLineItem: (invoiceId: string, itemId: string, body: UpdateLineItemPayload) =>
    request<{ item: InvoiceLineItem; invoice: InvoiceDetail }>(
      `/api/invoices/${invoiceId}/items/${itemId}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),
  deleteInvoiceLineItem: (invoiceId: string, itemId: string) =>
    request<{ success: boolean; invoice: InvoiceDetail }>(
      `/api/invoices/${invoiceId}/items/${itemId}`,
      { method: 'DELETE' },
    ),
  reorderInvoiceLineItems: (invoiceId: string, orderedIds: string[]) =>
    request<{ invoice: InvoiceDetail }>(`/api/invoices/${invoiceId}/items/reorder`, {
      method: 'POST',
      body: JSON.stringify({ orderedIds }),
    }),
  invoicePdfUrl: (id: string, opts: { format?: 'pdf' | 'zugferd'; download?: boolean } = {}) => {
    const p = new URLSearchParams()
    if (opts.format && opts.format !== PRIMARY_INVOICE_PDF_FORMAT) p.set('format', opts.format)
    if (opts.download) p.set('download', 'true')
    const qs = p.toString()
    return `${BASE_URL}/api/invoices/${id}/pdf${qs ? `?${qs}` : ''}`
  },
  getInvoiceMailState: (id: string, format: 'pdf' | 'zugferd' = PRIMARY_INVOICE_PDF_FORMAT) =>
    request<InvoiceMailState>(`/api/invoices/${id}/mail-state?format=${format}`),
  getInvoiceMailLog: (id: string) =>
    request<{ entries: InvoiceMailLogEntry[] }>(`/api/invoices/${id}/mail-log`),
  /** Preparation only: no mail, no claim, no dispatch record (#536). */
  previewInvoiceMail: (id: string, input: InvoiceMailPreviewInput) =>
    request<{ preview: InvoiceMailPreview }>(`/api/invoices/${id}/mail-preview`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  /** Never retried automatically; the attemptId makes a manual retry idempotent. */
  sendInvoiceMail: (id: string, input: InvoiceMailSendInput) =>
    request<InvoiceMailSendResult>(`/api/invoices/${id}/send-mail`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  checkInvoiceMailArchive: (id: string, attemptId: string) =>
    request<{ entry: InvoiceMailLogEntry }>(
      `/api/invoices/${id}/mail-log/${attemptId}/archive-check`,
      { method: 'POST' },
    ),
  /** Returns a Blob URL for a sample invoice rendered with the given draft
   * settings — caller is responsible for revoking the URL. */
  invoicePreviewLayoutBlob: async (override: Record<string, unknown>): Promise<string> => {
    const res = await apiFetch(`${BASE_URL}/api/invoices/preview-layout`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(override),
    })
    if (!res.ok) throw new Error(`Layout-Vorschau fehlgeschlagen (${res.status})`)
    const blob = await res.blob()
    return URL.createObjectURL(blob)
  },
  invoiceXRechnungUrl: (id: string) => `${BASE_URL}/api/invoices/${id}/xrechnung`,
  invoiceZugferdXmlUrl: (id: string) => `${BASE_URL}/api/invoices/${id}/zugferd-xml`,

  // Tickets
  listTickets: () => request<{ tickets: Ticket[] }>('/api/tickets'),
  getTicket: (id: string) => request<TicketDetail>(`/api/tickets/${id}`),
  createTicket: (data: {
    title: string
    description: string
    type?: TicketType
    priority?: TicketPriority
  }) =>
    request<{ ticket: Ticket }>('/api/tickets', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateTicket: (
    id: string,
    data: Partial<{
      title: string
      description: string
      type: TicketType
      priority: TicketPriority
      status: string
      columnId: string
      assignedTo: string | null
    }>,
  ) =>
    request<{ ticket: Ticket }>(`/api/tickets/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  deleteTicket: (id: string) =>
    request<{ ok: boolean }>(`/api/tickets/${id}`, { method: 'DELETE' }),
  moveTicket: (id: string, data: { columnId: string; position: number }) =>
    request<{ ticket: Ticket }>(`/api/tickets/${id}/move`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  uploadTicketAttachment: async (ticketId: string, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    const res = await apiFetch(`${BASE_URL}/api/tickets/${ticketId}/attachments`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    })
    if (!res.ok) {
      let m = `Upload failed (${res.status})`
      try {
        const j = (await res.json()) as { error?: string }
        if (j.error) m = j.error
      } catch {}
      throw new Error(m)
    }
    return (await res.json()) as { attachment: TicketAttachment }
  },
  ticketAttachmentUrl: (ticketId: string, attId: string) =>
    `${BASE_URL}/api/tickets/${ticketId}/attachments/${attId}/file`,
  deleteTicketAttachment: (ticketId: string, attId: string) =>
    request<{ ok: boolean }>(`/api/tickets/${ticketId}/attachments/${attId}`, {
      method: 'DELETE',
    }),
  listTicketComments: (ticketId: string) =>
    request<{ comments: TicketCommentWithUser[] }>(`/api/tickets/${ticketId}/comments`),
  addTicketComment: (ticketId: string, content: string) =>
    request<{ comment: TicketComment }>(`/api/tickets/${ticketId}/comments`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    }),
  deleteTicketComment: (ticketId: string, commentId: string) =>
    request<{ ok: boolean }>(`/api/tickets/${ticketId}/comments/${commentId}`, {
      method: 'DELETE',
    }),
  // Kanban
  listKanbanColumns: () => request<{ columns: KanbanColumn[] }>('/api/kanban/columns'),
  createKanbanColumn: (data: { name: string; color?: string | null }) =>
    request<{ column: KanbanColumn }>('/api/kanban/columns', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateKanbanColumn: (
    id: string,
    data: Partial<{ name: string; color: string | null; position: number }>,
  ) =>
    request<{ column: KanbanColumn }>(`/api/kanban/columns/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  deleteKanbanColumn: (id: string) =>
    request<{ ok: boolean }>(`/api/kanban/columns/${id}`, { method: 'DELETE' }),
  reorderKanbanColumns: (orderedIds: string[]) =>
    request<{ columns: KanbanColumn[] }>('/api/kanban/columns/reorder', {
      method: 'POST',
      body: JSON.stringify({ orderedIds }),
    }),
  getKanbanBoard: (
    opts: { type?: string; priority?: string; assignedTo?: string; search?: string } = {},
  ) => {
    const p = new URLSearchParams()
    if (opts.type) p.set('type', opts.type)
    if (opts.priority) p.set('priority', opts.priority)
    if (opts.assignedTo) p.set('assignedTo', opts.assignedTo)
    if (opts.search) p.set('search', opts.search)
    const qs = p.toString()
    return request<BoardData>(`/api/kanban/board${qs ? `?${qs}` : ''}`)
  },

  // AI Assistant
  getAiConfig: () => request<AiConfig>('/api/ai/config'),
  updateAiConfig: (data: {
    aiProvider?: string
    aiApiUrl?: string
    aiApiKey?: string
    aiModel?: string
    aiSystemPrompt?: string | null
    sttProvider?: string
    sttApiUrl?: string
    sttApiKey?: string
    sttModel?: string
  }) =>
    request<{ success: boolean }>('/api/ai/config', {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  aiChat: (messages: Array<{ role: string; content: string }>) =>
    request<AiChatResponse>('/api/ai/chat', {
      method: 'POST',
      body: JSON.stringify({ messages }),
    }),

  // Audit log (Verlauf)
  listAuditLog: (
    opts: {
      userId?: string
      resource?: string
      action?: string
      from?: string
      to?: string
      limit?: number
    } = {},
  ) => {
    const p = new URLSearchParams()
    if (opts.userId) p.set('user_id', opts.userId)
    if (opts.resource) p.set('resource', opts.resource)
    if (opts.action) p.set('action', opts.action)
    if (opts.from) p.set('from', opts.from)
    if (opts.to) p.set('to', opts.to)
    if (opts.limit) p.set('limit', String(opts.limit))
    const qs = p.toString()
    return request<{ entries: AuditEntry[] }>(`/api/audit-log${qs ? `?${qs}` : ''}`)
  },
  revertAuditEntry: (id: string) =>
    request<{ revertAuditId: string; restored: number }>(`/api/audit-log/${id}/revert`, {
      method: 'POST',
    }),

  // Invoice Settings
  getInvoiceSettings: () => request<{ settings: InvoiceSettings }>('/api/invoice-settings'),
  updateInvoiceSettings: (data: Partial<Omit<InvoiceSettings, 'id' | 'updatedAt'>>) =>
    request<{ settings: InvoiceSettings }>('/api/invoice-settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // ── Shifts ────────────────────────────────────────────────────────
  listShiftTypes: () => request<{ types: ShiftType[] }>('/api/shift-types'),
  createShiftType: (data: {
    name: string
    shortName: string
    color?: string
    startTime: string
    endTime: string
    breakMinutes?: number
  }) =>
    request<{ type: ShiftType }>('/api/shift-types', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateShiftType: (id: string, data: Partial<ShiftType>) =>
    request<{ type: ShiftType }>(`/api/shift-types/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  deleteShiftType: (id: string) =>
    request<{ ok: boolean }>(`/api/shift-types/${id}`, { method: 'DELETE' }),
  listShifts: (opts: { from?: string; to?: string; userId?: string } = {}) => {
    const p = new URLSearchParams()
    if (opts.from) p.set('from', opts.from)
    if (opts.to) p.set('to', opts.to)
    if (opts.userId) p.set('userId', opts.userId)
    const qs = p.toString()
    return request<{ assignments: ShiftAssignment[] }>(`/api/shifts${qs ? `?${qs}` : ''}`)
  },
  getShiftWeek: (from: string, to: string) =>
    request<{
      users: Array<{ id: string; name: string }>
      assignments: ShiftAssignment[]
      assignmentMap: Record<string, ShiftAssignment[]>
    }>(`/api/shifts/week?from=${from}&to=${to}`),
  createShift: (data: {
    userId: string
    shiftTypeId?: string | null
    date: string
    customStart?: string | null
    customEnd?: string | null
    notes?: string | null
  }) =>
    request<{ assignment: ShiftAssignment }>('/api/shifts', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateShift: (id: string, data: Record<string, unknown>) =>
    request<{ assignment: ShiftAssignment }>(`/api/shifts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  deleteShift: (id: string) => request<{ ok: boolean }>(`/api/shifts/${id}`, { method: 'DELETE' }),
  bulkCreateShifts: (assignments: Array<{ userId: string; date: string; shiftTypeId: string }>) =>
    request<{ assignments: ShiftAssignment[] }>('/api/shifts/bulk', {
      method: 'POST',
      body: JSON.stringify({ assignments }),
    }),

  // ── Inventory ─────────────────────────────────────────────────────
  listInventoryCategories: () =>
    request<{ categories: InventoryCategory[] }>('/api/inventory/categories'),
  createInventoryCategory: (data: { name: string; icon?: string | null; color?: string | null }) =>
    request<{ category: InventoryCategory }>('/api/inventory/categories', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateInventoryCategory: (
    id: string,
    data: Partial<{ name: string; icon: string | null; color: string | null }>,
  ) =>
    request<{ category: InventoryCategory }>(`/api/inventory/categories/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  deleteInventoryCategory: (id: string) =>
    request<{ ok: boolean }>(`/api/inventory/categories/${id}`, { method: 'DELETE' }),
  listInventory: (opts: { categoryId?: string; status?: string; search?: string } = {}) => {
    const p = new URLSearchParams()
    if (opts.categoryId) p.set('categoryId', opts.categoryId)
    if (opts.status) p.set('status', opts.status)
    if (opts.search) p.set('search', opts.search)
    const qs = p.toString()
    return request<{ items: InventoryItem[] }>(`/api/inventory${qs ? `?${qs}` : ''}`)
  },
  getInventoryItem: (id: string) =>
    request<{
      item: InventoryItem
      maintenanceLog: InventoryMaintenanceEntry[]
      locationHistory: InventoryLocationEntry[]
      dispositionEntries: DispositionEntry[]
    }>(`/api/inventory/${id}`),
  createInventoryItem: (data: Record<string, unknown>) =>
    request<{ item: InventoryItem }>('/api/inventory', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateInventoryItem: (id: string, data: Record<string, unknown>) =>
    request<{ item: InventoryItem }>(`/api/inventory/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  deleteInventoryItem: (id: string) =>
    request<{ ok: boolean }>(`/api/inventory/${id}`, { method: 'DELETE' }),
  addInventoryMaintenance: (
    id: string,
    data: { type?: string; description: string; costCents?: number | null },
  ) =>
    request<{ entry: InventoryMaintenanceEntry }>(`/api/inventory/${id}/maintenance`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  moveInventoryItem: (id: string, toLocation: string) =>
    request<{ entry: InventoryLocationEntry }>(`/api/inventory/${id}/move`, {
      method: 'POST',
      body: JSON.stringify({ toLocation }),
    }),
  inventoryQrUrl: (id: string) => `${BASE_URL}/api/inventory/${id}/qr`,

  // ── Disposition ───────────────────────────────────────────────────
  listDisposition: (
    opts: {
      from?: string
      to?: string
      userId?: string
      inventoryItemId?: string
      projectId?: string
    } = {},
  ) => {
    const p = new URLSearchParams()
    if (opts.from) p.set('from', opts.from)
    if (opts.to) p.set('to', opts.to)
    if (opts.userId) p.set('userId', opts.userId)
    if (opts.inventoryItemId) p.set('inventoryItemId', opts.inventoryItemId)
    if (opts.projectId) p.set('projectId', opts.projectId)
    const qs = p.toString()
    return request<{ entries: DispositionEntry[] }>(`/api/disposition${qs ? `?${qs}` : ''}`)
  },
  getDispositionTimeline: (from: string, to: string) =>
    request<{
      entries: DispositionEntry[]
      userEntries: Record<string, DispositionEntry[]>
      inventoryEntries: Record<string, DispositionEntry[]>
    }>(`/api/disposition/timeline?from=${from}&to=${to}`),
  createDisposition: (data: {
    title: string
    projectId?: string | null
    userId?: string | null
    inventoryItemId?: string | null
    startTime: string
    endTime: string
    location?: string | null
    notes?: string | null
    status?: string
    color?: string | null
  }) =>
    request<{ entry: DispositionEntry }>('/api/disposition', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateDisposition: (id: string, data: Record<string, unknown>) =>
    request<{ entry: DispositionEntry }>(`/api/disposition/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  deleteDisposition: (id: string) =>
    request<{ ok: boolean }>(`/api/disposition/${id}`, { method: 'DELETE' }),
  checkDispositionConflicts: (opts: {
    userId?: string
    inventoryItemId?: string
    startTime: string
    endTime: string
    excludeId?: string
  }) => {
    const p = new URLSearchParams()
    if (opts.userId) p.set('userId', opts.userId)
    if (opts.inventoryItemId) p.set('inventoryItemId', opts.inventoryItemId)
    p.set('startTime', opts.startTime)
    p.set('endTime', opts.endTime)
    if (opts.excludeId) p.set('excludeId', opts.excludeId)
    const qs = p.toString()
    return request<{ conflicts: DispositionEntry[]; hasConflicts: boolean }>(
      `/api/disposition/conflicts?${qs}`,
    )
  },

  // Fetcher (Invoice auto-download)
  listFetcherProviders: () => request<{ providers: FetcherProvider[] }>('/api/fetcher/providers'),
  listFetcherAccounts: () => request<{ accounts: FetcherAccount[] }>('/api/fetcher/accounts'),
  createFetcherAccount: (data: {
    provider: string
    label: string
    email: string
    password: string
    totpSecret?: string | null
    syncFromDate?: string | null
  }) =>
    request<{ account: FetcherAccount }>('/api/fetcher/accounts', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateFetcherAccount: (
    id: string,
    data: Partial<{
      label: string
      email: string
      password: string
      totpSecret: string | null
      syncFromDate: string | null
    }>,
  ) =>
    request<{ account: FetcherAccount }>(`/api/fetcher/accounts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  deleteFetcherAccount: (id: string) =>
    request<void>(`/api/fetcher/accounts/${id}`, { method: 'DELETE' }),
  syncFetcherAccount: (id: string) =>
    request<{ started: boolean; alreadySyncing: boolean }>(`/api/fetcher/accounts/${id}/sync`, {
      method: 'POST',
    }),
  submitFetcher2fa: (id: string, code: string) =>
    request<{ ok: boolean }>(`/api/fetcher/accounts/${id}/verify-2fa`, {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),
  listFetchedDocuments: (
    opts: { accountId?: string; type?: string; status?: string; from?: string; to?: string } = {},
  ) => {
    const p = new URLSearchParams()
    if (opts.accountId) p.set('accountId', opts.accountId)
    if (opts.type) p.set('type', opts.type)
    if (opts.status) p.set('status', opts.status)
    if (opts.from) p.set('from', opts.from)
    if (opts.to) p.set('to', opts.to)
    const qs = p.toString()
    return request<{ documents: FetchedDocument[] }>(`/api/fetcher/documents${qs ? `?${qs}` : ''}`)
  },
  updateFetchedDocumentStatus: (id: string, status: string) =>
    request<{ document: FetchedDocument }>(`/api/fetcher/documents/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),
  updateFetchedDocument: (
    id: string,
    data: Partial<{
      status: string
      documentNumber: string | null
      documentDate: string | null
      amountCents: number | null
      amountNetCents: number | null
      taxAmountCents: number | null
      taxRate: string | null
      vendorName: string | null
      type: string
    }>,
  ) =>
    request<{ document: FetchedDocument }>(`/api/fetcher/documents/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  bulkUpdateFetchedDocumentStatus: (ids: string[], status: string) =>
    request<{ updated: number }>('/api/fetcher/documents/bulk-status', {
      method: 'POST',
      body: JSON.stringify({ ids, status }),
    }),
  fetchedDocumentFileUrl: (id: string) => `${BASE_URL}/api/fetcher/documents/${id}/file`,

  // === Finanzplanung ===
  // Persönliche Haushaltsdaten: eigener User immer, fremde userId nur Admin
  // (Lese-Vollzugriff, Inhaber-Entscheid 2026-07-18). Mutationen owner-only.
  getFinanceBudget: (userId?: string) =>
    request<{ profile: FinanceProfile | null; items: FinanceBudgetItem[] }>(
      `/api/finance/budget${userId ? `?userId=${userId}` : ''}`,
    ),
  createFinanceBudgetItem: (data: {
    section: FinanceBudgetSection
    label: string
    monthlyCents?: number
  }) =>
    request<{ item: FinanceBudgetItem }>('/api/finance/budget-items', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateFinanceBudgetItem: (
    id: string,
    data: { label?: string; monthlyCents?: number; position?: number },
  ) =>
    request<{ item: FinanceBudgetItem }>(`/api/finance/budget-items/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  deleteFinanceBudgetItem: (id: string) =>
    request<{ success: boolean }>(`/api/finance/budget-items/${id}`, { method: 'DELETE' }),
  getFinanceCalculators: (userId?: string) =>
    request<{ calculators: FinanceCalculatorRow[] }>(
      `/api/finance/calculators${userId ? `?userId=${userId}` : ''}`,
    ),
  saveFinanceCalculator: (kind: FinanceCalculatorKind, inputs: Record<string, unknown>) =>
    request<{ calculator: FinanceCalculatorRow }>(`/api/finance/calculators/${kind}`, {
      method: 'PUT',
      body: JSON.stringify({ inputs }),
    }),
  // Project budget periods (independent of legacy Project.budgetHours).
  listBudgetPeriods: (projectId: string) =>
    request<{ periods: ProjectBudgetPeriod[] }>(`/api/projects/${projectId}/budget-periods`),
  createBudgetPeriod: (projectId: string, data: ProjectBudgetPeriodInput) =>
    request<{ period: ProjectBudgetPeriod }>(`/api/projects/${projectId}/budget-periods`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateBudgetPeriod: (
    projectId: string,
    periodId: string,
    data: Partial<ProjectBudgetPeriodInput>,
  ) =>
    request<{ period: ProjectBudgetPeriod }>(
      `/api/projects/${projectId}/budget-periods/${periodId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      },
    ),
  deleteBudgetPeriod: (projectId: string, periodId: string) =>
    request<{ success: boolean }>(`/api/projects/${projectId}/budget-periods/${periodId}`, {
      method: 'DELETE',
    }),
  getBudgetStatus: (projectId: string, options: { date?: string; userId?: string } = {}) => {
    const query = new URLSearchParams()
    if (options.date !== undefined) query.set('date', options.date)
    if (options.userId !== undefined) query.set('userId', options.userId)
    return request<ProjectBudgetStatus>(
      `/api/projects/${projectId}/budget-status${query.size ? `?${query}` : ''}`,
    )
  },
}

// === Finanzplanung — Typen ===
export type FinanceBudgetSection = 'income' | 'fixed' | 'variable' | 'insurance' | 'wealth'

export interface FinanceProfile {
  id: string
  userId: string
  createdAt: string
  updatedAt: string
}

export interface FinanceBudgetItem {
  id: string
  userId: string
  section: FinanceBudgetSection
  label: string
  monthlyCents: number
  position: number
  createdAt: string
  updatedAt: string
}

export type FinanceCalculatorKind = 'pension' | 'civil_pension'

export interface FinanceCalculatorRow {
  id: string
  userId: string
  kind: FinanceCalculatorKind
  /** Zod-validierte Rechner-Eingaben (Shape: shared PensionInputs bzw.
   *  CivilPensionInputs, jeweils + optionale params-Overrides). */
  inputs: Record<string, unknown>
  createdAt: string
  updatedAt: string
}

export interface ProjectBudgetPeriodInput {
  fromDate: string
  toDate: string
  hours: number
  note?: string | null
}

export interface ProjectBudgetPeriod extends ProjectBudgetPeriodInput {
  id: string
  projectId: string
  note: string | null
  createdAt: string
  updatedAt: string
}

export interface ProjectBudgetStatus {
  asOf: string
  userId: string
  period: Pick<ProjectBudgetPeriod, 'id' | 'fromDate' | 'toDate' | 'hours'> | null
  contingentHours: number | null
  usedHours: number | null
  remainingHours: number | null
  remainingWorkdays: number | null
  /** Unweighted daily rate; null without remaining workdays or an active period. */
  perDay: number | null
  /** Allowances for the REMAINING part of the current week/month. */
  weekHours: number | null
  monthHours: number | null
  /** Actual own billable totals for the whole day/week/month, clipped to the period.
   * Already included in usedHours; do not subtract again from the allowances. */
  ownTodayHours: number
  ownWeekHours: number
  ownMonthHours: number
}
